import { useEffect, useState, useCallback } from 'react'
import { businessTimeZone } from '../lib/businessTime'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Bell,
  RefreshCw,
  AlertCircle,
  MessageCircle,
  Database,
} from 'lucide-react'
import { useAlertStore } from '../stores/alertStore'
import { useAuthStore } from '../stores/authStore'
import {
  getInterviewStatus,
  getConciergeHome,
  type ConciergeHome,
} from '../lib/api'
import { SocShell } from '../components/soc/SocShell'
import { OnboardingInterviewPage } from './OnboardingInterviewPage'
import { AlertTimeline } from '../components/AlertTimeline'
import { HomeStatusCard } from '../components/HomeStatusCard'
import { HomeMailboxCoverageLine } from '../components/HomeMailboxCoverageLine'
import { HomeTeamCard } from '../components/HomeTeamCard'
import { HomeTripwiresCard } from '../components/HomeTripwiresCard'
import { IdleAdvisoryCard } from '../components/IdleAdvisoryCard'
import { HomeContextAskCard } from '../components/HomeContextAskCard'
import { ThisPollForwardEvidence } from '../components/ThisPollForwardEvidence'
import { ThisPollOauthGrantEvidence } from '../components/ThisPollOauthGrantEvidence'
import { SmbAllowStep } from '../components/SmbAllowStep'
import { cn, timeAgo } from '../lib/utils'
import {
  CLIENT_PLACE_INTENT_KEY,
  CLIENT_PLACED_DECOY_KEY,
  CLIENT_PLACE_ERROR_KEY,
  CLIENT_PLACE_HANDOFF_KEY,
} from '../lib/sessionKeys'

// DW-CLIENT-PLACE-BEATS-DASHBOARD-GATE: a client who opened /client to place a
// tripwire and signed in must not be shoved into the first-meeting interview
// before the decoy handoff (or its error) ever gets to paint. Any of these
// keys means LoginPage already tried (or is trying) to place on this
// client's behalf — that intent outranks an incomplete/unknown interview
// status for gate purposes. Never consumed here; HomeTripwiresCard owns
// consuming the intent/decoy/error keys (on sheet close, not on mount).
// CLIENT_PLACE_HANDOFF_KEY is never consumed by anyone but LoginPage's next
// sign-in — it is the sticky signal that survives after HomeTripwiresCard's
// sheet closes and drops the other three, so a later remount on this same
// /client visit still knows a place was attempted.
function hasPlaceHandoff(): boolean {
  return (
    !!sessionStorage.getItem(CLIENT_PLACE_INTENT_KEY) ||
    !!sessionStorage.getItem(CLIENT_PLACED_DECOY_KEY) ||
    !!sessionStorage.getItem(CLIENT_PLACE_ERROR_KEY) ||
    !!sessionStorage.getItem(CLIENT_PLACE_HANDOFF_KEY)
  )
}

// §7 honest alerting: this dashboard renders REAL alerts or an honest empty/
// error state. The old hardcoded demoAlerts fallback (five fabricated
// security alerts shown as if real) is deliberately gone — never reintroduce
// invented detections (TASKS/analyst-escalation-view.md).
//
// The calm-home headline is the LIVE Concierge status (HomeStatusCard) — the
// team's OWN plain-language reading of how things stand — rather than a verdict
// the FE derives from the raw alert list. The alert timeline below is the
// technical read-back beneath that story.
//
// First meeting gate (DW-INTERVIEW-FIRST-MEETING / DW-INTERVIEW-GATE-HOLD):
// if this principal has no completed interview, home IS the interview. Hold
// the console until status settles so incomplete principals never see a SIEM
// flash. Status is read-only — never call start from here (start after
// complete creates a new meeting forever).
//
// DW-CLIENT-HOME-FIRST-MEETING-KEEPS-WORD-DECOY-ON-HOME: "home IS the
// interview" was being implemented as a REDIRECT — an incomplete first
// meeting on /client was navigated off to /client/interview, which unmounted
// the home section with it. A household who authenticated onto /client
// therefore never saw HomeTripwiresCard, never saw "Download Decoy (.docx)",
// never saw "What we are holding for you": the first meeting became a second
// product that hid the canary. DigitalWatch.md: "Canaries are in the same
// picture — a tripwire the analyst sees with the logs, not a /client gimmick
// the model never reads." So on the exact /client path the gate now says
// `home` whatever the interview status is, and the leave is gone; the first
// meeting is still offered, in place, by ClientNavigation's held chrome
// (brand + "First meeting"), not by a redirect that costs the tripwire.
//
// The SIEM hold is UNCHANGED and is what the gate still exists for: on every
// other path this component mounts on (/client/alerts, /client/briefing) an
// incomplete or unknown status still LEAVES — the alert list, the this-poll
// evidence and the alerts fetch stay unreachable until the meeting is sat.
// Home is the team and the canaries; it is not the console, so there is no
// console to hold there.
//
// DW-CLIENT-HOME-DASHBOARD-NONHOME-LEAVE-KEEPS-WORD-DECOY-ON-HOME: that leave
// used to land on /client/interview, which cost the household the canary by
// the other door. A household who deep-linked (or was linked) to
// /client/alerts or /client/briefing with an unfinished meeting was dropped
// straight into the interview and never reached /client home at all: never saw
// HomeTripwiresCard, never saw "Download Decoy (.docx)", never saw "What we
// are holding for you". Same hole as the /client redirect above, entered from
// a non-home path — the first meeting became a second product that hid the
// canary. DigitalWatch.md: "Canaries are in the same picture — a tripwire the
// analyst sees with the logs, not a /client gimmick the model never reads."
// So the non-home leave now goes to /client, not /client/interview: the SIEM
// hold is exactly as strong (the console still never paints for an incomplete
// meeting, and the alerts fetch still never fires), and the household lands on
// the one holding picture with the Word decoy download on that home section.
// The first meeting is still offered, in place, by ClientNavigation's held
// chrome (brand + "First meeting") — never by a redirect that costs the
// tripwire, from any path.

// DW-CLIENT-HOME-FIRST-MEETING-IN-PLACE-KEEPS-WORD-DECOY-ON-HOME: keeping the
// household ON home closed the canary hole and opened another one — the
// meeting itself. Every door now settles on /client (this gate, the non-home
// leave, ClientInterviewGate, LoginPage, ClientNavigation's "First meeting"),
// and /client/interview replace-bounces an incomplete meeting back here, so a
// household who still needs the first meeting had nowhere left to sit it: they
// tapped "First meeting", stayed on home, and the questions never opened. The
// meeting had died so the canary could live. DigitalWatch.md asks for both:
// "Know the person — regular chat, patterns of life, where they sign in" AND
// "Canaries are in the same picture — a tripwire the analyst sees with the
// logs, not a /client gimmick the model never reads."
//
// So an incomplete first meeting is now SAT IN PLACE on home: when status
// explicitly says none/in_progress on exact /client, the meeting surface
// (OnboardingInterviewPage, embedded) mounts as a section of home, directly
// BELOW the tripwire band — never instead of it. HomeTripwiresCard stays
// mounted for the whole meeting, so "Download Decoy (.docx)" stays on that
// home section while they answer, and nobody is sent off home to be known.
//
// Three things it deliberately is not:
//   * not a console — the SIEM hold is untouched: incomplete still paints no
//     alert list, fires no alerts fetch and no this-poll fetch, on any path;
//   * not a start — an UNKNOWN status (the fetch threw) embeds nothing. We
//     never open a meeting we cannot confirm is unsat, and the embedded
//     surface re-reads status itself before it would ever POST start (start
//     after complete creates a new meeting forever). A complete household is
//     not embedded either: they still have /client/interview for bookmarks;
//   * not a login door — no meeting is ever started from a sign-in screen.
//
// DW-CLIENT-HOME-HOLDS-THE-FIREHOSE: "hold the firehose — 3-4 weeks of
// everything we already ingest: Microsoft 365 AND Google Workspace (sign-ins,
// audit, directory, mail rules, consents — NOT sign-ins alone)." Home says how
// much of that we are actually holding, as facts off the backend's `coverage`.
//
// Client words for the event types the backend names. This is a LABEL for a
// fact we already hold, not a fact of its own: a type we have no word for is
// rendered verbatim rather than dropped or guessed at, so the list can never
// be quietly narrowed to the kinds we happen to have phrasing for.
const COVERAGE_KIND_WORDS: Record<string, string> = {
  signin: 'sign-ins',
  audit: 'account activity',
  directory_change: 'directory changes',
  email_rule: 'mail rules',
  oauth_grant: 'app consents',
  alert: 'provider alerts',
}

function coverageKindWords(kinds: string[]): string {
  return kinds.map((kind) => COVERAGE_KIND_WORDS[kind] ?? kind).join(', ')
}

/** Console paint gate: hold until interview status explicitly says complete. */
type InterviewGate = 'pending' | 'home' | 'leave'

// The held kinds, read out as one English phrase: "a", "a and b",
// "a, b and c". A joined sentence, never a list — the teaser stays a thing we
// SAY, not an inventory the household is meant to work through.
function joinKinds(kinds: string[]): string {
  if (kinds.length <= 1) return kinds.join('')
  return `${kinds.slice(0, -1).join(', ')} and ${kinds[kinds.length - 1]}`
}

export function ClientDashboard() {
  const navigate = useNavigate()
  const location = useLocation()
  // DW-CLIENT-HOME-IS-TEAM-NOT-SIEM (GO_LIVE): exact /client is home — the
  // team, tripwires, and the one holding picture. Every other path it mounts
  // on (/client/alerts, /client/briefing) is the honest technical read-back:
  // the alert list and the this-poll evidence, and only that.
  const isHome = location.pathname === '/client'
  const { alerts, fetchAlerts, isLoading, error } = useAlertStore()
  const principalId = useAuthStore((s) => s.principal?.id)
  const plainMember = useAuthStore((s) => s.principal?.plain_member)
  const [isRefreshing, setIsRefreshing] = useState(false)
  // Unknown stays unknown: console body paints only once status explicitly
  // says complete. No principal → status is not consulted; hold the shell.
  const [gate, setGate] = useState<InterviewGate>('pending')
  // Team brief (v93 live home paint): fetched once the gate says home. Honest
  // failure on error — never invent ticker lines or a fabricated team roster.
  const [home, setHome] = useState<ConciergeHome | null>(null)
  const [homeError, setHomeError] = useState<string | null>(null)
  // DW-CLIENT-PLACE-BEATS-DASHBOARD-GATE: captured once at mount, not
  // re-read live on every render. HomeTripwiresCard consumes (removes) these
  // sessionStorage keys the moment it mounts, so a live hasPlaceHandoff()
  // call in the render body would flip back to false — unmounting the card
  // the instant it consumed its own handoff, then remounting it fresh (and
  // losing the decoy/error it just showed) once the team brief arrives.
  const [hadPlaceHandoff] = useState(() => hasPlaceHandoff())
  // DW-CLIENT-HOME-FIRST-MEETING-IN-PLACE-KEEPS-WORD-DECOY-ON-HOME: true only
  // when status EXPLICITLY came back not-complete on exact /client. Unknown
  // (a status throw) and "no principal" both stay false — an unproven meeting
  // is never opened on home. Set false again by the embedded warm close, so a
  // meeting finished in place leaves home standing as home.
  const [meetingOnHome, setMeetingOnHome] = useState(false)
  // DW-CLIENT-HOME-POL-KINDS-WE-HOLD: the kinds of patterns of life behind
  // the count, exactly as the backend read them off the rows it counted.
  // Anything that is not a non-empty string is dropped rather than painted —
  // this page never renders a raw stored value, and an older brief that
  // carries no kinds at all simply leaves the teaser on the count alone.
  const polKinds = (home?.pol_summary_kinds ?? []).filter(
    (kind): kind is string => typeof kind === 'string' && kind.trim() !== '',
  )
  // DW-CLIENT-HOME-POL-KINDS-IN-HOLDING-PICTURE: the count itself, held to
  // the same rule as every other line of the holding picture — a positive
  // number we were actually sent, or nothing at all. A count that is not a
  // number is UNKNOWN (an older brief, or one that failed) and paints no
  // line rather than a fabricated zero; a count of 0 is a household we have
  // written nothing about yet, and silence is the honest read of that. It is
  // never rounded up into "we know your people" we cannot back.
  const heldPolCount =
    home &&
    typeof home.pol_summary_count === 'number' &&
    home.pol_summary_count > 0
      ? home.pol_summary_count
      : null

  useEffect(() => {
    if (!principalId) {
      // DW-CLIENT-PLACE-PAINTS-WITHOUT-PRINCIPAL: a JWT place already
      // happened on this /client visit (decoy / error / intent in
      // sessionStorage) — paint home so HomeTripwiresCard can show it even
      // though getMe left principal (and so principalId) null. No principal
      // → no interview status to consult either way; only the gate changes.
      setGate(hadPlaceHandoff ? 'home' : 'pending')
      // No principal → no status was read, so no meeting is known to be unsat.
      setMeetingOnHome(false)
      return
    }
    let cancelled = false
    setGate('pending')
    setMeetingOnHome(false)
    void (async () => {
      try {
        const st = await getInterviewStatus()
        if (cancelled) return
        // The first meeting is SAT here, in place, when it is genuinely unsat
        // and this is home (DW-CLIENT-HOME-FIRST-MEETING-IN-PLACE-KEEPS-WORD-
        // DECOY-ON-HOME). A complete meeting embeds nothing — that household
        // keeps /client/interview for bookmarks; a non-home path embeds
        // nothing either — it leaves for home, where the meeting lives with
        // the canary.
        setMeetingOnHome(isHome && st.status !== 'complete')
        if (st.status === 'complete' || hadPlaceHandoff || isHome) {
          setGate('home')
        } else {
          // DW-CLIENT-HOME-DASHBOARD-NONHOME-LEAVE-KEEPS-WORD-DECOY-ON-HOME:
          // an incomplete meeting on a non-home path leaves for /client home,
          // never for /client/interview. The console still never paints (gate
          // is 'leave', so no alert list and no alerts fetch) — the household
          // just lands on the holding picture with the Word decoy download,
          // instead of on an interview that hides the canary.
          setGate('leave')
          navigate('/client', { replace: true })
        }
      } catch {
        // A status throw is not a completed meeting → the meeting is home,
        // UNLESS a sign-in-to-place handoff is waiting — that client came to
        // place a tripwire, not to sit a first meeting, and a flaky status
        // check must not steal that handoff into the interview. On /client
        // itself an unknown status is likewise no reason to leave (see
        // DW-CLIENT-HOME-FIRST-MEETING-KEEPS-WORD-DECOY-ON-HOME above): a
        // flaky status check must never cost the household the canary either.
        if (cancelled) return
        // Unknown is not an unsat meeting: a flaky status check must never
        // open a first meeting we cannot prove is owed (and must never risk a
        // start against a household who already sat one).
        setMeetingOnHome(false)
        if (hadPlaceHandoff || isHome) {
          setGate('home')
        } else {
          // Same leave as the incomplete branch: an unknown status off home
          // holds the console and lands on /client home, so a flaky status
          // check cannot cost the household the Word decoy from a non-home
          // path either (DW-CLIENT-HOME-DASHBOARD-NONHOME-LEAVE-KEEPS-WORD-
          // DECOY-ON-HOME).
          setGate('leave')
          navigate('/client', { replace: true })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [principalId, navigate, hadPlaceHandoff, isHome])

  // Do not fetch alerts (or mount alert chrome) until the gate says home —
  // and only on the alerts read-back path, never on the /client team page.
  useEffect(() => {
    if (gate !== 'home' || isHome) return
    void fetchAlerts()
  }, [fetchAlerts, gate, isHome])

  // Fetch the team brief once the gate says home, on the /client team page
  // only — the alerts read-back page does not show the team/ticker/tripwires
  // band, so it has no need of this fetch. Honest failure: clear any stale
  // brief and say so — never invent ticker lines or team blurbs.
  useEffect(() => {
    if (gate !== 'home' || !principalId || !isHome) return
    let cancelled = false
    setHome(null)
    setHomeError(null)
    void (async () => {
      try {
        const data = await getConciergeHome(principalId)
        if (!cancelled) setHome(data)
      } catch (err) {
        if (!cancelled) {
          setHome(null)
          setHomeError(err instanceof Error ? err.message : 'unknown error')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [gate, principalId, isHome])

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    await fetchAlerts()
    setIsRefreshing(false)
  }, [fetchAlerts])

  // Incomplete / still resolving: no console body flash. Nav chrome optional;
  // empty shell keeps the next paint the meeting, not a SIEM home.
  if (gate !== 'home') {
    return <SocShell held>{null}</SocShell>
  }

  // GO_LIVE: /client/alerts (and any other non-/client path this component
  // still mounts on, e.g. /client/briefing) is the honest technical
  // read-back — this-poll evidence, then the alert list. It is the list;
  // home is the team. No team/ticker/tripwires band here — that stays on
  // /client so this is not a second dashboard, just the demoted console.
  if (!isHome) {
    return (
      <SocShell mainClassName="box-border w-full max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <h1 className="text-xl font-serif text-navy mb-5">Alerts</h1>

          {/* This-poll evidence — same BLIND surfaces the analyst sees, in
              client voice. Only mounted once the gate says home, so incomplete
              first meetings never trigger the this-poll fetches. Empty or
              failed fetch → nothing renders. */}
          <ThisPollForwardEvidence audience="client" />
          <ThisPollOauthGrantEvidence audience="client" />

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-navy flex items-center gap-2">
              <Bell className="w-5 h-5 text-brass" aria-hidden="true" />
              What we noticed
            </h2>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="tap-min p-2 rounded-lg text-gray-400 hover:text-brass hover:bg-brass/10 disabled:opacity-50 transition-colors"
              aria-label="Refresh alerts"
            >
              <RefreshCw
                className={cn('w-4 h-4', isRefreshing && 'animate-spin')}
                aria-hidden="true"
              />
            </button>
          </div>

          {/* Honest error state — never fabricated alerts */}
          {error ? (
            <div className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-4 text-sm text-danger">
              <AlertCircle
                className="w-4 h-4 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <span>
                We could not load your alerts right now ({error}). Nothing is
                shown because nothing could be loaded.
              </span>
            </div>
          ) : (
            <AlertTimeline
              alerts={alerts}
              isLoading={isLoading}
              onRefresh={handleRefresh}
            />
          )}
      </SocShell>
    )
  }

  // DW-CLIENT-CANARIES-IN-THE-SAME-PICTURE: canaries are not a gadget bolted
  // on beside the picture — they are one more thing the team HOLDS for this
  // client, read off the same team brief as the 365 + Workspace coverage and
  // said in the same block. Steve, 3 Sep: the tripwire box reads as a SIEM
  // widget; the canary belongs in the team picture, as facts.
  //
  // Facts only, and only the facts the backend actually sent:
  //   * `active_count` > 0 is the ONLY thing that puts a tripwire line in the
  //     picture. Zero — or no brief at all — paints nothing here: we never
  //     invent a planted canary, and the tripwires section above is already
  //     the door for setting the first one;
  //   * `fired_count` is optional on the wire (an older engine omits it), so
  //     a missing field is UNKNOWN and paints no fired story — never a
  //     reassuring "nothing has ever fired" we did not verify;
  //   * an unparseable `last_fired_at` costs the "most recently …" clause
  //     only. The fire itself is still said; a bad timestamp never invents a
  //     time and never swallows the fact.
  //
  // DW-CLIENT-HOME-WORD-DECOY-FIRE-IS-THE-ONE-HOLDING-TRIPWIRES-ROW: these two
  // now feed the "Your tripwires" ROW of that same list rather than a second
  // cream box under it — a fire is the Word document they download, opened, so
  // it is said where the documents are said. Same fields, same rules, one
  // surface.
  const heldTripwires =
    home && home.tripwires.active_count > 0 ? home.tripwires : null
  const firedCount =
    typeof heldTripwires?.fired_count === 'number'
      ? heldTripwires.fired_count
      : 0
  const lastFiredAt = heldTripwires?.last_fired_at
  const lastFiredWhen =
    firedCount > 0 && lastFiredAt && !Number.isNaN(Date.parse(lastFiredAt))
      ? timeAgo(lastFiredAt)
      : null
  // The firehose half of the same picture, unchanged: 365 + Workspace still
  // paint on exactly the days the backend counted, and a canary can never
  // displace or shrink them.
  const holdsCoverageDays =
    home?.coverage?.platforms?.some((platform) => platform.days_held > 0) ??
    false
  // DW-CLIENT-HOME-CURRENT-CTI-SAME-PICTURE: the intel desk is the third kind
  // of thing we hold, so it is said in the SAME list as 365/Workspace and the
  // canaries — "analyse that picture against known TTPs and current CTI", not
  // a news clipping beside the picture. Both values come off `intel_desk`,
  // which the backend composed from the brief row it already read and the
  // ticker it already returned; nothing here adds a CVE, a campaign or a
  // detection of its own.
  //
  // Honest by construction:
  //   * the field is optional on the wire. Missing (no brief, or the desk
  //     applied nothing here) is UNKNOWN — no line at all, never a soothing
  //     "0 threats this week";
  //   * a count that is not a positive number paints nothing, so the line can
  //     only ever say work that exists;
  //   * an unparseable `brief_date` costs the "as of …" clause only — the
  //     lines held are still said, and no date is invented to fill it in.
  const heldIntelDesk =
    home?.intel_desk &&
    typeof home.intel_desk.applied_line_count === 'number' &&
    home.intel_desk.applied_line_count > 0
      ? home.intel_desk
      : null
  const intelDeskAsOf =
    heldIntelDesk?.brief_date &&
    !Number.isNaN(Date.parse(heldIntelDesk.brief_date))
      ? new Date(heldIntelDesk.brief_date).toLocaleDateString(undefined, {
          timeZone: businessTimeZone(),
          day: 'numeric',
          month: 'short',
        })
      : null
  // DW-CLIENT-HOME-INTEL-DESK-LINES-ARE-THE-ONE-HOLDING-INTEL-ROW: the desk's
  // LINES, for the row above to be. DigitalWatch.md: "expensive model, right
  // prompt — analyse that picture against known TTPs and CURRENT CTI (intel
  // desk / deep research), not a leftover rule stamp"; GO_LIVE: "a security
  // team that knows the client, not a SIEM the client operates", and the story
  // lives on /client home as ONE holding picture.
  //
  // Current CTI used to live on TWO surfaces: a watch card in the band above
  // ("What your analyst is watching") printing these very lines, and a row of
  // "What we are holding for you" saying only how many of them there were.
  // One desk, told twice — a widget to operate beside a tally in the picture,
  // which is exactly the SIEM shape the sign-in, mail-rule/consent,
  // audit/directory and Word-decoy folds already closed. So the lines
  // themselves are now the row, and the card is gone from home.
  //
  // Nothing is fetched, counted or composed for this: these are `home.ticker`,
  // the SAME list the dead card printed, rendered VERBATIM. So:
  //   * the row is the LINES where we hold lines — never a count standing in
  //     for them, which is the gimmick the model never reads;
  //   * NO LINES → the row falls back to the honest count/silence it already
  //     painted off `intel_desk` (and no `intel_desk` is no row at all). There
  //     is no fallback line, no "quiet week", and — load-bearing — no
  //     campaign, CVE, kit or detector invented to fill the gap;
  //   * nothing is re-worded, trimmed, ranked or summarised on the way in. The
  //     only lines dropped are non-strings and blanks, which are not lines;
  //   * it is a fact we hold, not a console we hand back: no button, no link,
  //     and nobody is sent to /client/world to work their own intel out;
  //   * reading this week's intel against what we hold is LOOKING. The row
  //     claims no catch, no block and no action — response stays fail-closed.
  const heldTickerLines = (home?.ticker ?? []).filter(
    (line): line is string =>
      typeof line === 'string' && line.trim().length > 0,
  )
  // DW-CLIENT-HOME-KNOWN-TTPS-SAME-PICTURE: the other half of the product
  // loop — "analyse that picture against KNOWN TTPs and current CTI". The
  // intel desk line above says what the world is doing; this says how much of
  // THIS household's own activity was actually read against what attackers
  // are known to use, and how much of it lined up. Same list, after the desk.
  // Both numbers come off `known_ttps`, which the backend counted off stamps
  // already stored on this tenant's judged batches; nothing here adds an
  // indicator, a CVE, a campaign or a detection of its own.
  //
  // Honest by construction:
  //   * the field is optional on the wire. Missing — nothing matched, or an
  //     older engine — is UNKNOWN, so no line at all, never a reassuring
  //     "0 known TTPs seen" we did not verify;
  //   * both counts must be positive numbers and the match must fit inside
  //     what was compared, so the numbers the client reads can never be a
  //     nonsense pair the FE assembled;
  //   * a match is a comparison that lined up. The row says that in English
  //     and stops — no catch, no block, no response is claimed anywhere in
  //     it, and no technique, indicator or campaign is named.
  const heldKnownTtps =
    home?.known_ttps &&
    typeof home.known_ttps.matched_count === 'number' &&
    typeof home.known_ttps.compared_count === 'number' &&
    home.known_ttps.matched_count > 0 &&
    home.known_ttps.compared_count >= home.known_ttps.matched_count
      ? home.known_ttps
      : null
  // DW-CLIENT-HOME-ESCALATE-IF-UNSURE: the LAST sentence of the product loop —
  // "escalate to a dearer SOC agent when unsure; act only within the armed/
  // disarmed policy". Every line above says what we hold and what we read it
  // against. This says what happens to the part we cannot close, which is the
  // whole difference between a team and a rule. Both numbers come off
  // `escalate_if_unsure`, which the backend counted off judgement rows it
  // already stores; nothing here adds a conclusion, a signal or a verdict.
  //
  // Honest by construction:
  //   * the field is optional on the wire. Missing — nothing was left unsure,
  //     or an older engine — is UNKNOWN, so no line at all, never a soothing
  //     "0 escalations" we did not verify;
  //   * both counts must be positive numbers and the unsure count must fit
  //     inside what was judged, so the fraction the client reads can never be
  //     a nonsense one the FE assembled;
  //   * going to a person is NOT acting. This line says how many went up and
  //     stops — no catch, no block, no response is claimed anywhere in it, and
  //     it says out loud that we did not act.
  const heldEscalateIfUnsure =
    home?.escalate_if_unsure &&
    typeof home.escalate_if_unsure.unsure_count === 'number' &&
    typeof home.escalate_if_unsure.judged_count === 'number' &&
    home.escalate_if_unsure.unsure_count > 0 &&
    home.escalate_if_unsure.judged_count >= home.escalate_if_unsure.unsure_count
      ? home.escalate_if_unsure
      : null
  // DW-CLIENT-HOME-HOLDING-PICTURE-MAIL-RULE-CONSENT-ENGLISH: the product
  // page's True list — "we can notice new mail rules and new consents, we can
  // talk in English". The chat door already promises that English, and the
  // chat already speaks it. But a client who never starts a chat reads the
  // holding picture instead, and the picture only names KINDS: "mail rules",
  // "app consents". That is inventory — a SIEM the client operates — and it
  // is the same records saying the same thing, in a language they did not ask
  // for. So the picture says it in English too, on the home they open.
  //
  // DW-CLIENT-HOME-MAIL-RULE-CONSENT-ENGLISH-NOT-A-SECOND-BOX: and it says it
  // ONCE. That English was a second cream box under the holding list while
  // the list already named these kinds on the 365 / Workspace rows — the same
  // records on two surfaces, which is the SIEM the client operates. These
  // flags now paint a ROW of "What we are holding for you" instead. Same
  // records, same sentences, one picture.
  //
  // The kinds are already on the coverage payload the backend counted, so
  // this reads what is in hand — no second fetch, no new count, no new DB
  // read, nothing recomputed and no detector named.
  //
  // Honest by construction:
  //   * the story exists only where the KIND exists. A household whose
  //     coverage names neither kind gets NO line — and, load-bearing, no
  //     "there isn't one" either. An absence we did not verify is not an
  //     all-clear, and the kinds tell us what we hold, never what is not
  //     there;
  //   * mail and access are separate facts and are said separately. Holding
  //     mail-rule records never speaks for consents, and the reverse;
  //   * NO rule, NO address, NO app, NO mailbox and NO person is named,
  //     because the payload holds none of them here and printing one would be
  //     the invention the rule itself forbids;
  //   * NO PLATFORM is named. The kinds arrive per platform, but saying which
  //     cloud would tell a household we hold one of them something about a
  //     cloud they may not have — the kinds are pooled and the story is said
  //     once;
  //   * it claims NO action. Saying a rule out loud is not taking it off, and
  //     the line says so in the product's own disarmed words: we did not act.
  const coverageKinds = new Set(
    home?.coverage?.platforms?.flatMap((platform) => platform.kinds ?? []) ??
      [],
  )
  const holdsMailRuleRecords = coverageKinds.has('email_rule')
  const holdsConsentRecords = coverageKinds.has('oauth_grant')
  const holdsMailRuleOrConsent = holdsMailRuleRecords || holdsConsentRecords

  // DW-CLIENT-HOME-HOLDING-PICTURE-AUDIT-DIRECTORY-ENGLISH: the same rule,
  // for the two kinds of the product loop's second line that the picture
  // still only names as inventory — "sign-ins, audit, directory, mail rules,
  // consents, NOT sign-ins alone". The chat already reads an administrative
  // change and a directory change back in English; the holding picture said
  // "account activity, directory changes" and stopped. A client who never
  // starts a chat therefore got a SIEM brochure for that half of the
  // firehose, from records we already hold.
  //
  // Read off the SAME pooled coverage kinds as the mail-rule/consent story
  // above — no second fetch, no new count, no new DB read, no detector.
  //
  // Honest by construction, on exactly the same terms as its neighbour:
  //   * the story exists only where the KIND exists. Coverage naming neither
  //     kind gets NO line, and — load-bearing — no "there isn't one" either.
  //     The kinds say what we hold, never what is not there;
  //   * an administrative change and a change to the directory are separate
  //     facts and are said separately. Holding audit records never speaks for
  //     directory changes, and the reverse;
  //   * NO operation, NO role, NO group, NO mailbox and NO person is named,
  //     because the payload holds none of them here;
  //   * NO PLATFORM is named — the kinds are pooled across the clouds we hold
  //     and the story is said once, so a household on one cloud is never told
  //     something about a cloud they do not have;
  //   * it claims NO action, and says so in the product's own disarmed words:
  //     we did not act, and nothing has been put back the way it was.
  //
  // DW-CLIENT-HOME-AUDIT-DIRECTORY-ENGLISH-NOT-A-SECOND-BOX: and, like its
  // neighbour, it says it ONCE. That English was a second cream box under the
  // holding list while the list already named these kinds on the 365 /
  // Workspace rows — the same records on two surfaces, which is the SIEM the
  // client operates. These flags now paint a ROW of "What we are holding for
  // you" instead. Same records, same sentences, one picture.
  const holdsAuditRecords = coverageKinds.has('audit')
  const holdsDirectoryRecords = coverageKinds.has('directory_change')
  const holdsAuditOrDirectory = holdsAuditRecords || holdsDirectoryRecords

  // DW-CLIENT-HOME-HOLDING-PICTURE-SIGNINS-ENGLISH: the FIRST named kind of
  // the product loop's second line — "sign-ins, audit, directory, mail rules,
  // consents, NOT sign-ins alone" — and the one the loop's FIRST line is
  // about: "know the person ... WHERE THEY SIGN IN." Its neighbours above
  // already speak their held records in English on the home a client opens.
  // Sign-ins did not: the picture printed the kind word "sign-ins" and
  // stopped, so the part of the picture that is most about the person was
  // the part that read most like a SIEM the client operates.
  //
  // Read off the SAME pooled coverage kinds as both stories above — no second
  // fetch, no new count, no new DB read, no detector named.
  //
  // Honest by construction, on the same terms as its neighbours, plus the one
  // rule that is only this line's:
  //   * the story exists only where the KIND exists. Coverage that does not
  //     name it gets NO line and — load-bearing — no "there isn't one"
  //     either. The kinds say what we hold, never what is not there;
  //   * NO INVENTED PLACE. The mouth does not invent cities: no device, no
  //     address and no person is named, because the coverage kind holds none
  //     of those. A city is named ONLY when the brief already carries it as a
  //     recorded fact (see the places rule below) — never composed, never
  //     derived from the tenant, the profile or a country;
  //   * NO PLATFORM — the kinds are pooled across the clouds we hold and the
  //     story is said once, so a household on one cloud is never told
  //     something about a cloud they do not have;
  //   * A SIGN-IN GOING THROUGH IS NOT SAFE. MFA success is never safe: the
  //     line says out loud that passing every check is not proof of who it
  //     was, so this can never be read as an all-clear on the sign-ins we do
  //     hold;
  //   * it claims NO action, and no response: nobody was signed out and no
  //     sign-in was stopped. Response stays fail-closed.
  const holdsSignInRecords = coverageKinds.has('signin')

  // DW-CLIENT-HOME-HOLDING-PICTURE-SIGNIN-PLACES-FACTS: the product loop's
  // first line, finished — "know the person ... WHERE THEY SIGN IN.
  // Graph/Workspace locations are FACTS. The mouth does not invent cities."
  // The sign-in English above told a household we hold recorded places for to
  // ASK THEIR TEAM for the places — while those very labels were already on
  // this same payload, and already painted in the teaser further up the page.
  // Sending a client away for a fact we are holding on the page in front of
  // them is a SIEM the client operates; this line makes home the team that
  // knows them and says the fact.
  //
  // Read off the SAME brief the teaser reads (`signin_locations.places`) — no
  // second fetch, no new count, no new DB read, no detector named.
  //
  // Honest by construction:
  //   * every label is the backend's, rendered VERBATIM. Nothing here
  //     composes, shortens, title-cases, geocodes or completes a place; the
  //     only labels dropped are non-strings and blanks, which are not places;
  //   * the list is said to be exhaustive of what we hold, which is what the
  //     chat already tells the client — home and the mouth agree;
  //   * NO PLACES on the payload → the sign-in English stays, unchanged and
  //     silent on cities. It does NOT invent one, does NOT fall back to a
  //     country, and — load-bearing — does NOT say "no places" or "there
  //     isn't one", because an absence we did not verify is not an all-clear;
  //   * naming where they signed in is never naming it as safe: the MFA line
  //     below it still says a sign-in going through is not proof it was them,
  //     and nothing here is a control. We did not act.
  const heldSignInPlaces = (home?.signin_locations?.places ?? []).filter(
    (place): place is string =>
      typeof place === 'string' && place.trim().length > 0,
  )
  const heldSignInPlacesEnglish =
    heldSignInPlaces.length > 1
      ? `${heldSignInPlaces.slice(0, -1).join(', ')} and ${
          heldSignInPlaces[heldSignInPlaces.length - 1]
        }`
      : heldSignInPlaces.join('')

  return (
    // BRIEF-E5d: the one customer header. While a first meeting is sat here
    // in place, its menu is held back: those pages wait for the meeting.
    <SocShell held={meetingOnHome} mainClassName="box-border w-full max-w-5xl mx-auto px-4 sm:px-6 py-6">

      {/* DW-CLIENT-HOME-IS-TEAM-NOT-SIEM (GO_LIVE): home is the team that
          knows the client, not a SIEM the client operates. One wide page:
          the team brief band across the top (team, what the analyst is
          watching, tripwire coverage), then the team's live reading and the
          quieter asks. The alert list and this-poll evidence are analyst
          machinery wearing client clothes — they live at /client/alerts,
          not here. */}
        <h1 className="text-xl font-serif text-navy mb-5">Your dashboard</h1>

        {/* DW-CLIENT-HOME-OFFERS-REGULAR-CHAT: "know the person — REGULAR
            CHAT, patterns of life, where they sign in." Chat is the first
            clause of the product sentence, so home says out loud that this is
            a team you talk to, and opens the chat that already exists at
            /client/concierge. The customer should not have to guess the URL.

            Honest by construction: this is a DOOR, not a conversation. It
            renders no transcript, no greeting attributed to the team, no
            "all clear", no place and no count — nothing that would imply the
            team has already spoken on this visit. It carries no payload
            field, so it stands whether or not the team brief loaded: the
            chat is reachable even on a bad brief day. The reply itself comes
            from the concierge behind that door, never from here.

            DW-CLIENT-CHAT-ANALYSES-THE-PICTURE: the door now says what is on
            the other side of it. The concierge chat is handed the SAME
            holding picture this page paints — the 365 / Workspace window, the
            tripwires, the intel desk, the known-TTP read and what went to a
            person — so "they are already looking at what we hold for you" is
            a fact about the product, not a promise this page makes up. It
            stays a claim about the TEAM, carries no payload field and no
            count, and so is equally true on a bad brief day. It claims no
            action: they are looking, not acting. */}
        <section className="mb-5 rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-brass" aria-hidden="true" />
            <h2 className="font-serif text-lg text-navy">Talk to your team</h2>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            There are people behind this. Ask them anything in your own words:
            whether an email is real, why something looks odd, what a message
            from us meant. No ticket, no jargon.
          </p>
          <p className="mt-2 text-sm text-gray-600">
            They are already looking at what we hold for you, the same picture
            as this page.
          </p>
          {/* DW-CLIENT-CHAT-FIREHOSE-WEEKS-IN-FRONT-OF-THE-MODEL: "hold the
              firehose — capture and keep IN FRONT OF THE MODEL 3-4 weeks of
              everything we already ingest". The line above was true of the
              COUNTS: the chat was handed how many days and which kinds we
              hold. It is now true of the weeks themselves — the concierge
              turn carries a bounded sample of the stored 365 / Workspace
              records from that same window, both clouds and every kind we
              hold, so the team reads the records and not a tally.

              Said the same careful way as the lines around it: a claim about
              the TEAM, no payload field, so it stands on a bad brief day. It
              names NO count (that is the coverage card's job below), NO
              platform and NO kind — telling a 365-only household about their
              mail rules or their Google would be invention one field over.
              And it claims no action: being given the records is looking. */}
          <p className="mt-2 text-sm text-gray-600">
            Not just how much we hold: they are given the records themselves
            from the weeks we keep, not a summary of counts.
          </p>
          {/* DW-CLIENT-CHAT-CANARIES-IN-THE-MODEL-PICTURE: "canaries are in
              the same picture — a tripwire the analyst sees WITH THE LOGS,
              not a gimmick the model never reads." The chat was handed the
              tripwire COUNTS beside those weeks; it is now handed the fires
              themselves — what we wrote at the time and when — in the same
              turn as the records above. So the door may say the decoys are
              read with the weeks, and not as a number on their own.

              Said the same careful way as the lines around it. It is a claim
              about the TEAM, carries no payload field and so stands on a bad
              brief day; it names NO count, NO decoy and NO date; and it is
              CONDITIONAL — "if one is ever opened" — because saying anything
              else would tell a household with no fire that they had one. It
              claims no action: the fire goes to them, and nothing was
              stopped. */}
          <p className="mt-2 text-sm text-gray-600">
            If one of your tripwires is ever opened, that goes to them with
            those same weeks — read together, not as a number on its own.
          </p>
          {/* DW-CLIENT-CHAT-INTEL-DESK-LINES-IN-FRONT-OF-THE-MODEL: the
              product page says "expensive model, right prompt — analyse that
              picture against known TTPs and CURRENT CTI (intel desk / deep
              research)". The chat was handed the desk as a date and a count
              of how many of its lines your team applied here; it is now
              handed those lines themselves — the exact ones this page prints
              in the holding picture below, on its own intel-desk row — in the
              same turn as the records and the fires. So the door may say this week's intel reaches them as
              lines, not as a tally.

              Said the same careful way as the lines around it. It is a claim
              about the TEAM, carries no payload field and so stands on a bad
              brief day; it names NO count, NO date and — the load-bearing one
              — NO THREAT: naming a campaign here would be the invention the
              ticker itself is filtered to prevent. And reading this week's
              intel against what we hold is looking: it claims no action. */}
          <p className="mt-2 text-sm text-gray-600">
            This week's intel reaches them as the lines themselves — the same
            ones on this page — read with those weeks, not as a count.
          </p>
          {/* DW-CLIENT-CHAT-KNOWN-TTP-MATCHES-IN-FRONT-OF-THE-MODEL: the
              other half of the same product line — "analyse that picture
              against KNOWN TTPs and current CTI". The chat was handed the
              known-TTP read as a fraction: how many batches of your own
              activity we compared, how many lined up. It is now handed what
              lined up BY NAME — our own name for the activity and the kind of
              indicator it matched — in the same turn as the records, the
              fires and this week's lines. So the door may say the techniques
              reach them as techniques, not as a tally.

              Said the same careful way as the lines around it. It is a claim
              about the TEAM, carries no payload field and so stands on a bad
              brief day; it names NO count and NO technique — naming one here
              would tell a household nothing lined up for that something did;
              and it is CONDITIONAL for that same reason. The indicator itself
              is not on this page and is not in their prompt. And it claims no
              action: something lining up is a comparison, not a catch. */}
          <p className="mt-2 text-sm text-gray-600">
            If your own activity ever lines up with a technique attackers are
            known to use, they are told which one — read with those weeks, not
            as a tally.
          </p>
          {/* DW-CLIENT-CHAT-ANALYSES-THE-PICTURE-AGAINST-CTI-AND-TTPS: the
              verb in that same product line — "analyse THAT picture against
              known TTPs and current CTI, not a leftover rule stamp". Every
              line above says something REACHES them: the records, the fires,
              this week's lines, the techniques. Being handed four things is
              not reading one against another, and the door would have been
              describing a reading room. The chat now carries the instruction
              to compare them in the same turn, so the door may say the team
              reads what we hold for you against what we read it against.

              Said the same careful way as its neighbours. It is a claim about
              the TEAM, carries no payload field and so stands on a bad brief
              day; it names NO count, NO date, NO platform, NO place and — the
              load-bearing one — NO THREAT AND NO TECHNIQUE, because naming
              one here would be the invention the comparison itself is fenced
              against. It is not conditional in the way the lines above are:
              the instruction to compare is in every conversation, which is
              why it may be said in the present tense. And it claims no
              action: reading your weeks against this week's intel is looking,
              and the line after it still says we do not act. */}
          <p className="mt-2 text-sm text-gray-600">
            Those are not read as separate lists. Your weeks and your tripwires
            are read against this week's intel and the techniques that lined up
            — and what they tell you comes only from those records, never a name
            from somewhere else.
          </p>
          {/* DW-CLIENT-CHAT-PATTERNS-OF-LIFE-IN-THE-MODEL-PICTURE: the FIRST
              line of the product loop — "know the person: regular chat,
              patterns of life, where they sign in". What we know about this
              household has been in front of the chat for a while, and the
              line above tells it to compare their weeks with what the world
              is doing. Both true, and still a stranger's console: nothing
              said to read a sign-in, a mail rule or a quiet hour against how
              THIS household actually lives, so the answer came back as a
              generic finding about an anonymous mailbox. The chat now carries
              one rule putting their own rhythm first — their weeks are read
              against how their world normally runs before they are read
              against anything else.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO platform, NO place, NO threat and NO
              technique; and it names nothing of this household's own life,
              because printing a person, an hour or a tool here would be the
              invention the rule itself forbids. It is not conditional — the
              rule is in every conversation — and it says the honest half out
              loud: what we have not been told, they will not guess. Reading
              them is looking: it claims no action. */}
          <p className="mt-2 text-sm text-gray-600">
            That reading starts with you, not with the internet: your weeks are
            read against what we have been told about how your world normally
            runs — the people, the hours, the tools — before they are read
            against anything else. If we have not been told, they will say so
            rather than guess.
          </p>
          {/* DW-CLIENT-CHAT-ESCALATE-IF-UNSURE-IN-FRONT-OF-THE-MODEL: the
              last line of the product loop — "escalate to a dearer SOC agent
              when unsure; act only within the armed/disarmed policy". The
              chat was handed that sentence as arithmetic: how many batches we
              judged, how many did not close. It is now handed the times
              themselves — what each one was about and when we were unsure —
              in the same turn as the records, the fires, this week's lines
              and the techniques. So the door may say the part we could not
              settle reaches them too.

              Said the same careful way as the lines around it. It is a claim
              about the TEAM, carries no payload field and so stands on a bad
              brief day; it names NO count, NO date and NO subject; and it is
              CONDITIONAL — "if something ever leaves us unsure" — because
              saying anything else would tell a household with a quiet month
              that we were troubled by them. What we suspected is not on this
              page and is not in their prompt. And it is the one line here
              that must be explicit about the disarmed policy: being unsure
              goes to a PERSON, and nothing is acted on. */}
          <p className="mt-2 text-sm text-gray-600">
            If something of yours ever leaves us unsure, they are told what it
            was about and when — read with those weeks, not as a tally. Being
            unsure goes to a person; nothing is acted on for you.
          </p>
          {/* DW-CLIENT-CHAT-ANALYSIS-THAT-CANNOT-SETTLE-ESCALATES: the JOIN
              between the two lines above. The line before last says the team
              reads your weeks against this week's intel; the last one says
              the times we were not sure reach them too. Both were true and
              they were still two lists — nothing said what the reading DOES
              when it runs out of road, so the only endings available were the
              two the product forbids: fill in the half we were never handed,
              or hand the gap back as "we compared it and it came back clean".
              The chat now carries one rule joining them: a comparison that
              cannot settle — a missing half, or something we read and could
              not close — goes to a person for a dearer read, and is never a
              clean bill.

              So the door may say that, and says it the same careful way as
              its neighbours: a claim about the TEAM, no payload field, so it
              stands on a bad brief day; NO count, NO date, NO platform, NO
              place and — load-bearing — NO THREAT AND NO TECHNIQUE, because
              naming one here would be the invented catch the rule itself
              forbids. It promises nothing about what that person will find.
              And it is explicit that going to a person is a dearer READ and
              not a response: nothing is acted on, which the line after it
              says again without an "if". */}
          <p className="mt-2 text-sm text-gray-600">
            When that reading cannot settle something, we say so: it goes to a
            person for a dearer read, never back to you as a clean bill. Nothing
            is acted on for you while they read it.
          </p>
          {/* DW-CLIENT-CHAT-FAIL-CLOSED-POLICY-IN-FRONT-OF-THE-MODEL: the
              other half of that same last line — "act only within the
              armed/disarmed policy (response stays fail-closed until we mean
              it)". Every line above this one is CONDITIONAL: it begins "if",
              or it describes something we were handed. So on a quiet month —
              nothing unsure, no fire, nothing lined up — the only place the
              team behind this door had ever been told we do not act was a
              rule hanging off rows that were not there, and the door said it
              only inside an "if". The chat now carries the policy itself in
              every turn, read off the same switch the part of the product
              that would carry out an action reads.

              So this line is the one that is NOT conditional, and it is the
              only one that may be read as a promise about today: we are
              disarmed, and the sentence is present tense for that reason —
              the day the policy changes, this line changes with it. Said the
              same careful way as its neighbours: a claim about the TEAM, no
              payload field, so it stands on a bad brief day; no count, no
              date, no platform and no place; and it claims nothing was done,
              because nothing was. */}
          <p className="mt-2 text-sm text-gray-600">
            We do not act on your account — not on a quiet week and not on a
            busy one. They are given that policy in every conversation, so they
            will tell you what we can see and what we recommend, and you decide
            what happens next.
          </p>
          {/* DW-CHAT-MOUTH-DOES-NOT-INVENT-CITIES: the product page says
              "Graph/Workspace locations are FACTS. The mouth does not invent
              cities." The places on this page come off the sign-ins Microsoft
              and Google recorded, and the chat behind this door is now held to
              the same list in code — a city we have not seen is left out of
              the answer, never filled in with a plausible one.

              So the door may say it. Like the lines above it: a claim about
              how the TEAM works, carrying no payload field, no count and — on
              purpose — no place at all, so it is equally true on a bad brief
              day and on a household we hold no city for. It claims no action:
              naming where they sign in is looking, not acting. It also names
              no PLATFORM: "Microsoft and Google" here would tell a household
              we only hold 365 for that we hold Workspace too, which is the
              same invention one field over. */}
          <p className="mt-2 text-sm text-gray-600">
            Where you sign in comes from the sign-in records themselves. If we
            have not seen a place, they will not name one.
          </p>
          {/* DW-CLIENT-CHAT-SIGNIN-LOCATIONS-IN-THE-MODEL-PICTURE: the JOIN
              between the two lines above. One says your weeks are read
              against how your world normally runs; the other says a place we
              have not recorded is never named. Both true, and still leaving
              the gap this line closes: "how your world normally runs" is what
              you TOLD us — a home town, an office — so "your usual places"
              could quietly mean a town nobody ever signed in from while the
              recorded sign-ins sat unread. The chat now carries one rule
              putting those two kinds of fact in their places: what we recorded
              is where you signed in; what you told us about your life is not.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO platform and — the load-bearing one — NO
              PLACE, because printing one here would be the invention the rule
              itself forbids. It is not conditional: the rule is in every
              conversation. And reading where you sign in is looking, so it
              claims no action. */}
          <p className="mt-2 text-sm text-gray-600">
            That record is what your weeks are read against, too: when they tell
            you whether something looks usual for you, the places in that answer
            are ones we have actually seen you sign in from — never a town you
            once told us about, and never one we hold no record of.
          </p>
          {/* DW-CLIENT-CHAT-MAIL-RULES-AND-CONSENTS-IN-THE-MODEL-PICTURE:
              the SECOND line of the product loop — "hold the firehose: 3-4
              weeks of everything we already ingest (sign-ins, audit,
              directory, mail rules, consents — NOT sign-ins alone)". The
              coverage block further down already says we KEEP those kinds.
              Every reading line above it, though, is written in the language
              of weeks, hours and places, so the answer could come back about
              where you signed in while a new mail rule and a new app
              permission sat in the very same window, recorded and unread. The
              chat now carries one rule joining them: a mail rule or a
              permission WE RECORDED is read in the same answer as the rest.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO place and — the load-bearing one — NO
              PLATFORM, because naming one here would tell a household we only
              hold Microsoft 365 for that we read their Google Workspace too,
              which is the same invention one field over. It names no rule, no
              address and no app, because printing one would be the invention
              the rule itself forbids. It is not conditional — the rule is in
              every conversation — and reading what changed is looking, so it
              claims no action. */}
          <p className="mt-2 text-sm text-gray-600">
            Sign-ins are not the whole of it. New mail rules and new app
            permissions we have recorded on your accounts are read in that same
            answer, alongside your weeks — and one we hold no record of is never
            named.
          </p>
          {/* DW-CLIENT-CHAT-AUDIT-AND-DIRECTORY-IN-THE-MODEL-PICTURE:
              the SAME second line of the product loop, and the two kinds it
              names that nothing above had joined yet — "sign-ins, audit,
              directory, mail rules, consents". The coverage block further
              down already says we KEEP account activity and directory
              changes. Every reading line above it, though, is written in the
              language of weeks, hours, places and mailboxes, so the answer
              could come back about where you signed in and what happened to
              your mail while an administrative change and a change to who
              exists on your accounts sat in the very same window, recorded
              and never spoken. The chat now carries one rule joining them: an
              admin change or a directory change WE RECORDED is read in the
              same answer as the rest, on both clouds.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO place and — the load-bearing one — NO
              PLATFORM, because naming one here would tell a household we only
              hold Microsoft 365 for that we read their Google Workspace
              directory too, which is the same invention one field over. It
              names no operation, no role, no group and no person, because
              printing one would be the invention the rule itself forbids. It
              is not conditional — the rule is in every conversation — and
              reading what an administrator did is looking, so it claims no
              action. */}
          <p className="mt-2 text-sm text-gray-600">
            Nor is your mail. Administrative activity on your accounts, and
            changes to who is on them and what they are allowed, are read in
            that answer too — whichever of your workplace accounts we recorded
            them on, and one we have no record of is never named.
          </p>
          {/* DW-CLIENT-CHAT-MFA-SUCCESS-NEVER-SAFE-IN-THE-MODEL-PICTURE:
              the product page keeps a short NOT-the-product list, and one of
              its entries is three words — "MFA success = safe". Every line
              above says another kind of record is read in the same answer,
              and each one made the cheap ending likelier: the sign-ins went
              through, so they proved it was them, so we can stand down —
              said over the top of a new mail rule and an administrative
              change sitting in the very same window. A stolen session
              arrives already past the second step; that is the whole reason
              it is taken. The chat now carries one rule pinning the reading:
              a sign-in that went through is one more record in the picture,
              never the sentence that ends it.

              This line has to say the honest half too, and that is the
              load-bearing part: correcting "multi-factor means safe" is NOT
              a licence to promise the opposite. The page's own "not true
              yet" line is that an analyst would have caught MFA-success
              session theft — so this door says plainly that it is not
              something we catch, and promises nothing about finding it.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO place and NO PLATFORM, because naming one
              here would tell a household we only hold one cloud for that we
              held the other's second step too. It is not conditional — the
              rule is in every conversation — and it claims no action: the
              disarmed line above still stands. */}
          <p className="mt-2 text-sm text-gray-600">
            And a sign-in going through is not the same as being safe. Passing
            your second step is read as one more record in that answer, never as
            proof it was you — theft that walks past it is not something we
            catch, so they will not hand you an all-clear on those grounds.
          </p>
          {/* DW-CLIENT-CHAT-DO-NOT-CLAIM-ANALYST-CAUGHT-MFA-THEFT: the
              product page's "what we may say to clients today" block, and the
              line under it that is NOT yet true — "an analyst lived in your
              weeks of logs and would have caught MFA-success session theft".
              The line above says the theft is real and that a sign-in going
              through is not an all-clear; that is exactly the moment the
              reassurance gets reached for — someone has been over these
              weeks, we have had eyes on this the whole time, we would have
              caught it. We hold a KEPT record and read it when you ask;
              nobody watched it as it arrived, and finding theft that walks
              past your second step is not something this product does yet.
              Honesty is the product, so the door says it before the chat
              has to.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO place and NO PLATFORM, because naming one
              here would tell a household we hold one cloud for that we had
              been living in the other's weeks. It is not conditional — the
              rule is in every conversation — and it claims no action: the
              disarmed line above still stands. */}
          <p className="mt-2 text-sm text-gray-600">
            And nobody has been sitting over those weeks as they arrived. We
            keep them, and your team reads them when you ask — so they will not
            tell you someone has been living in your logs, and they will not
            claim they would have caught a theft that walked past your second
            step.
          </p>
          {/* DW-CLIENT-CHAT-MAY-SAY-ONLY-WHAT-IS-TRUE-TODAY: the OTHER
              list on the product page — "True: we connect 365/GWS, we can
              notice new mail rules and new consents, we can talk in English,
              canaries exist in the product". The line above refuses ONE
              sentence, and refusing one sentence is not keeping to a list:
              the reassurance simply moves one word sideways into we watch
              every mailbox like an analyst, we catch the video, we will spot
              the next one. So the door says the list itself, closed, and says
              that nothing past its end is sold — "selling we catch the video"
              is on the page's own NOT-the-product list until the loop is live
              on a real mailbox.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO place and NO PLATFORM, because naming a cloud
              here would tell a household we connect one of them for that we
              had connected the other too. It is not conditional — the rule is
              in every conversation — and it claims no action: the disarmed
              line above still stands. */}
          <p className="mt-2 text-sm text-gray-600">
            What they can do today is a short list, and they will say it
            plainly: we connect your workplace accounts and keep the weeks they
            record, we can notice a new mail rule or a new app permission that
            was recorded, we can talk any of it through with you in plain
            English, and the tripwire documents are real — when one is opened,
            the fire comes back to us. Nothing past the end of that list gets
            sold to you.
          </p>
          {/* DW-CLIENT-CHAT-NO-SIEM-RULE-LIBRARY: the FIRST line of the
              product page's NOT-the-product list — "a SIEM rule library (no
              Kali365 detector, no kit-name rule)" — and the second half of
              loop line 4, "analyse that picture against known TTPs and
              current CTI, not a leftover rule stamp".

              Every line above fences a CLAIM. None of them fences the answer
              to "how do you watch us?", which is what a client actually
              asks — and the stock answer in security writing is a detector
              name: a rule fired, our library covers that, the rule for that
              did not trigger so you are fine. That is a different product,
              and it is backwards: the picture is READ, against tradecraft
              and this week's intel, not stamped with a name left over from
              someone else's job. The chat now carries one rule saying so, so
              this door says it too — and it names no detector, no rule and
              no kit itself, because printing the name you are forbidding
              teaches it.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO place and NO PLATFORM, because naming a
              cloud here would tell a household we hold one of them that we
              read the other too. It is not conditional — the rule is in
              every conversation — and it claims no action: the disarmed line
              above still stands. */}
          <p className="mt-2 text-sm text-gray-600">
            And when you ask how they watch, they will not answer with the name
            of a detector, a rule or a kit — there is no rulebook here to match
            you against, and none to grow. They keep your weeks and read them
            against the tradecraft we know and what the intel desk is seeing
            this week, then tell you what they make of it.
          </p>
          {/* DW-CLIENT-CHAT-DO-NOT-GROW-THE-RULEBOOK: the product page's
              builder law — "Anvil/Forge implement this loop. They do not grow
              the rulebook."

              The line above refuses to NAME a detector. It does not cover
              what happens next, which is the obvious question back: then add
              one for me, put a watch on that kit, turn an alert on for this.
              The generous-sounding answer — I can add a rule for that, our
              library does not cover that yet but we can — hands them a
              rulebook with a hole in it and a promise to close the hole, and
              that is a SIEM the client operates rather than a team that
              knows them. So the door says the refusal out loud, and says it
              without a maybe-later, because a backlog is a yes with a date
              on it.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO place and NO PLATFORM, because naming a
              cloud here would tell a household we hold one of them that we
              read the other too. It is not conditional — the rule is in
              every conversation — and it claims no action: the disarmed line
              above still stands. */}
          <p className="mt-2 text-sm text-gray-600">
            And if you ask them to add a rule for it, or to put a watch on
            something you have read about, they will say no — kindly, and with
            no maybe-later on the end of it. There is nothing here for them to
            add it to. What they do instead is take the thing you are worried
            about and read it against what your own records actually show.
          </p>
          {/* DW-CLIENT-CHAT-WORD-DECOY-IN-THE-MODEL-PICTURE: the THIRD line
              of the product page's loop — "canaries are in the same picture
              — a tripwire the analyst sees WITH THE LOGS, not a /client
              gimmick the model never reads."

              The tripwire lines above say a fire reaches your team with your
              weeks. None of them says what the tripwire IS, and on this
              product it is very specific: the Word document the card below
              hands you, which you download and save yourself, in a folder
              you pick. Left unsaid, the artefact you are holding and the
              thing your team talks about are two different objects — which
              is the gimmick the page names. The chat now carries one rule
              telling it the live decoy is that document, so this door says
              it too.

              Said the same careful way as its neighbours: a claim about the
              TEAM and the handoff, no payload field, so it stands on a bad
              brief day; NO count, NO date, NO place and NO PLATFORM, and no
              file name or folder name — yours is yours to name. The fire
              half is CONDITIONAL — "if it is ever opened" — because saying
              anything else would tell a household with no fire that they had
              one. And it claims no action: it is read, not acted on. */}
          <p className="mt-2 text-sm text-gray-600">
            And when they say tripwire, they mean something you can hold: the
            Word document you download from this page and save wherever you keep
            your files. You choose the folder — we never touch your storage. If
            it is ever opened, that comes back to them and gets read with your
            weeks, not on a screen of its own.
          </p>
          {/* DW-CLIENT-CHAT-OPENED-WORD-DECOY-FIRE-IN-THE-MODEL-PICTURE: the
              same THIRD line of the loop, one step further on. The line
              above says a fire comes back with your weeks; it does not say
              what your team calls it once it lands. Left there, the honest
              answer to "has anything of mine been opened?" is still "a
              tripwire fired" — a mechanism of ours going off, sitting beside
              your weeks instead of inside them, while the thing you actually
              placed was a document you saved yourself. That is the gimmick
              the page names, wearing the fire as a costume. So the chat now
              carries one rule saying an opened decoy IS that document, read
              in the same breath as the weeks, and this door says it too.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO place and NO PLATFORM, and no file name or
              folder name. The fire half is CONDITIONAL — "if it is ever
              opened" — because saying anything else would tell a household
              with no fire that they had one. And it claims no action: seen,
              never stopped. */}
          <p className="mt-2 text-sm text-gray-600">
            And if it is ever opened, they will not talk to you about a tripwire
            going off somewhere — it is that document of yours, read in the same
            breath as your weeks. Something they saw, not something that stopped
            anybody.
          </p>
          {/* DW-CLIENT-CHAT-DO-NOT-SELL-CATCHING-THE-VIDEO: the LAST line of
              the product page's NOT-the-product list — "selling 'we catch the
              video' before this loop is live on a real mailbox".

              Every line above has been honest, and that is exactly where the
              catch gets asked for. Once you have been told a document of
              yours was opened, the next question is not "did an analyst live
              in my weeks" — it is: is there a video? does the camera have
              them? did you record it? would we have got them on tape? The
              warm answer to any of those is a capability this product does
              not have and never had. There is no camera and no recording:
              there is a line your cloud wrote down afterwards, and a document
              of yours being opened. The loop that would make a catch real is
              not live on a real mailbox yet, so selling it is the page's own
              last not-the-product line. The chat now carries one rule
              refusing it, and this door says it before the chat has to.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO place and NO PLATFORM, because naming a cloud
              here would tell a household we hold one of them that we had the
              other's week too. It is not conditional — the rule is in every
              conversation — and it claims no action: the disarmed line above
              still stands. */}
          <p className="mt-2 text-sm text-gray-600">
            And they will not sell you a catch. There is no video, no recording,
            no camera and no tape — what we hold is what your accounts wrote
            down afterwards, and the fact that a document of yours was opened. A
            record is not a recording. The wider loop that would let anyone
            promise more than that is still being built and is not switched on
            for you, so they will tell you what they can see and nothing past
            it.
          </p>
          {/* DW-CLIENT-CHAT-LOOP-NOT-LIVE-ON-A-REAL-MAILBOX: the CONDITION on
              that same last line of the product page's NOT-the-product list —
              "selling 'we catch the video' BEFORE THIS LOOP IS LIVE ON A REAL
              MAILBOX" — and the page's "not true until the loop above is
              live".

              The line above refuses the catch, and it carries this condition
              inside itself as one clause about the sale. But the condition is
              not a footnote on the sale, it is the gate — and it fails on its
              own words. A client who never asks about a video still asks: so
              are you watching my mailbox now? is this running yet? am I on
              it? None of those is a request for a catch, so the line above
              never meets them, and the warm answer to every one of them —
              yes, we are watching; it is switched on; you are in beta; any
              day now — sells the loop itself. It is not live on a real
              mailbox yet, and yours is a real mailbox. So the door says the
              gate in its own words, before the chat has to.

              Said the same careful way as its neighbours: a claim about the
              TEAM and the product, no payload field, so it stands on a bad
              brief day; NO count, NO date — a date is the same promise with a
              delay on it — NO place and NO PLATFORM, because naming a cloud
              here would tell a household we hold one of them that the other
              was running too. It is not conditional — the rule is in every
              conversation — and it claims no action: the disarmed line above
              still stands. */}
          <p className="mt-2 text-sm text-gray-600">
            And they will not tell you this is already running. The wider loop
            we are building — your weeks read the way a security team reads
            them, and handed to a person when it will not settle — is not live
            on a real mailbox yet, and yours is a real mailbox. It has not been
            switched on for you, nobody is sitting over your accounts as they
            happen, and there is no date we can honestly give you. What is on
            today is the short list above, and that is what your team will talk
            to you about.
          </p>
          {/* DW-CLIENT-CHAT-ANSWERS-FROM-THE-WEEKS-WE-HOLD: the product
              page's second and fourth loop lines — "hold the firehose ...
              keep IN FRONT OF THE MODEL 3-4 weeks of everything we already
              ingest" and "expensive model, right prompt — ANALYSE THAT
              PICTURE ... not a leftover rule stamp".

              Every line above this one is honest, and read together they are
              a wall. The chat already holds the weeks, the fires, the intel
              desk and the tradecraft, and it already says what it will not
              claim. So the plainest question a customer has — what happened
              this week? did you notice anything? anything in my mail? — can
              be answered entirely out of the refusals: not switched on, no
              catch, nobody living in your weeks. Every sentence true, and not
              one record read. That is a brochure with good manners. The chat
              now carries one rule sending the answer back to the records that
              were in the turn the whole time, and this door says so.

              Said the same careful way as its neighbours: a claim about the
              TEAM and the product, no payload field, so it stands on a bad
              brief day; NO count, NO date, NO place and NO PLATFORM, because
              naming a cloud here would tell a household we hold one of them
              that we had the other's week too. It promises no reading of a
              week we do not hold — an empty window is said empty — it does
              not walk back the line above it, and it claims no action:
              reading is looking, not doing. */}
          <p className="mt-2 text-sm text-gray-600">
            But when you ask what happened — what they noticed, whether anything
            looked odd, what was in your mail — they answer from the weeks we
            hold for you, not from a list of what we will not claim. They read
            the records themselves and tell you what is in them. If we have not
            got those weeks, they will say so plainly instead of making a week
            up. None of that means anyone was sitting over your accounts as it
            happened, and none of it is us acting for you.
          </p>
          {/* DW-CLIENT-CHAT-MAIL-RULE-AND-CONSENT-IN-ENGLISH: the product
              page's True list — "we can notice new mail rules and new
              consents, we can talk in English".

              The line above already sends the answer to the records instead
              of the refusals, and that is still a claim about our STORAGE.
              Asked "anything in my mail?" or "who did we let in?", the answer
              could come back as weeks, kinds and days — every word honest —
              and never once say the plain thing the record already says: a
              rule was made on your mail, an app was given access. That is the
              English the True list promises, and this door says they speak
              it.

              Said the same careful way as its neighbours: a claim about the
              TEAM, no payload field, so it stands on a bad brief day; NO
              count, NO date, NO place and — the load-bearing one — NO
              PLATFORM, because naming a cloud here would tell a household we
              hold one of them that we read the other. It names no rule, no
              address and no app, because printing one would be the invention
              the rule itself forbids. An absence is said, never turned into
              "there isn't one". And it claims no action: saying it out loud
              is not taking it off. */}
          <p className="mt-2 text-sm text-gray-600">
            And if what you asked about is your mail or who has access, they say
            it in plain English rather than in records: a rule was made on your
            mail, or an app was given access to your account — only the ones we
            have actually recorded, and never one named that we have not. They
            will tell you in the same breath that we have not touched it: no
            rule taken off, no permission taken back, nobody signed out. And if
            we are holding no record of that kind, they will say that plainly
            instead of telling you there is nothing there.
          </p>
          <button
            type="button"
            onClick={() => navigate('/client/concierge')}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-navy px-4 py-2 text-sm text-white transition-opacity hover:opacity-90"
          >
            Start a chat
          </button>
        </section>

        {/* Team brief (v93 live home paint) — team roster, tripwire count,
            and the desk lines the holding picture below prints. Honest
            failure if the fetch didn't succeed; never a fabricated brief. */}
        {homeError && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-4 text-sm text-danger mb-5"
          >
            <AlertCircle
              className="w-4 h-4 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <span>
              We could not load your team&apos;s brief right now ({homeError}
              ).
            </span>
          </div>
        )}
        {/* The home band: the team, then tripwires — home's own sections,
            side by side on a wide screen instead of stacked in a phone
            column. `[&>section]:mb-0` folds each card's own stacking margin
            into the grid gap so the row lines up.

            DW-CLIENT-HOME-INTEL-DESK-LINES-ARE-THE-ONE-HOLDING-INTEL-ROW:
            the this-week watch card ("What your analyst is watching") used to
            sit in this band, between the two, printing the desk's lines —
            while "What we are holding for you" below already carried the very
            same desk as a count. One desk on two surfaces: a widget to
            operate up here, a tally down there. The card is gone from home and
            its lines ARE the intel-desk row of that one picture now. The
            component itself is untouched and still tested on its own; nothing
            on home mounts it.

            DW-CLIENT-TRIPWIRES-ARE-A-HOME-SECTION: tripwires sits in this
            band unconditionally — it is a section of home, not a door that
            only opens for a place handoff, so it must not be the one card
            painting when the rest of the brief is missing. It also keeps the
            single JSX slot the place handoff needs
            (DW-CLIENT-PLACE-BEATS-DASHBOARD-GATE): one slot regardless of
            `home`, so HomeTripwiresCard never unmounts/remounts (and loses
            its decoy / auto-place state) the moment the brief arrives.

            No brief → no team card, no desk row, and null coverage rather
            than a fabricated zero: an unknown count is said out loud by the
            card, never rounded down to "No tripwires in place yet." */}
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 [&>section]:mb-0 mb-5">
          {home && <HomeTeamCard team={home.team} />}
          {/* Tripwires are the owner's; a plain member never reads how they work. */}
          {!plainMember && (
            <HomeTripwiresCard tripwires={home ? home.tripwires : null} />
          )}
        </div>

        {/* DW-CLIENT-HOME-FIRST-MEETING-IN-PLACE-KEEPS-WORD-DECOY-ON-HOME:
            the first meeting, sat in place. It sits BELOW the band above, so
            HomeTripwiresCard is never unmounted, never displaced and never
            replaced — the household answers "who are you" with "Download
            Decoy (.docx)" still on screen, in the same picture, exactly as
            DigitalWatch.md asks ("know the person" AND "canaries are in the
            same picture"). It is the SAME meeting as /client/interview, the
            same server-scripted questions in the Concierge's voice: embedded,
            not re-written, and never a second product.

            It is not a console: this block paints only home's own sections —
            the alert list is still nowhere on /client, and an incomplete
            meeting still fires no alerts fetch on any path. The warm close
            hands back here, so a meeting finished in place simply leaves
            home. */}
        {meetingOnHome && (
          <section aria-label="Your first meeting" className="mb-5">
            <h2 className="text-lg font-semibold text-navy mb-3">
              Your first meeting
            </h2>
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <OnboardingInterviewPage
                embedded
                onDone={() => setMeetingOnHome(false)}
              />
            </div>
          </section>
        )}

        {/* DW-CLIENT-HOME-HOLDS-THE-FIREHOSE: "hold the firehose — capture
            and keep in front of the model 3-4 weeks of everything we already
            ingest: Microsoft 365 AND Google Workspace (sign-ins, audit,
            directory, mail rules, consents — not sign-ins alone)." Coverage is
            the client-facing half of that promise: how many of the last N days
            of each platform we can actually look back through.

            Honest by construction. Every number and every platform name here
            came off `home.coverage`, which the backend counted from the events
            it holds. This block:
              * renders ONLY the platforms the backend sent — a family with no
                Google Workspace has no Google entry, and nothing here adds
                one. Google is never invented;
              * says 0 as 0. A connected platform we hold nothing for reads
                "Nothing held yet", never rounded up to a plausible week;
              * paints nothing at all when every platform is 0 (or the list is
                empty, or the brief failed) — an empty promise is worse than
                silence, and the brief-failure band above already says when we
                could not load it;
              * names the kinds we hold, so the claim cannot quietly shrink to
                sign-ins: if sign-ins are all we have, this says exactly that.

            DW-CLIENT-CANARIES-IN-THE-SAME-PICTURE: the tripwires we hold are
            the same kind of fact, so they are said here too — in the same
            list, under the same heading, after 365 and Workspace. A canary
            never displaces a platform line and never shrinks the window; it
            only ADDS the count we hold, and, if one fired, the team's
            existing disarmed story. The section may now also paint for a
            client we hold tripwires but no days for — that is still a real
            thing held, not an empty promise.

            DW-CLIENT-HOME-POL-KINDS-IN-HOLDING-PICTURE: and the person is in
            the same picture too. The patterns of life we hold — the count and
            the kinds behind it — are one more line of this list rather than a
            second card, so a household reads 365, Workspace, their tripwires
            AND what we know about their world under one heading. Same reason
            the section paints for tripwires-without-days: a household we hold
            a written world for but no days and no decoys still has something
            real held, and this is where they read it. */}
        {home &&
        (holdsCoverageDays ||
          heldTripwires ||
          heldPolCount ||
          holdsSignInRecords ||
          heldSignInPlaces.length ||
          heldTickerLines.length) ? (
          <section className="mb-5 rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-brass" aria-hidden="true" />
              <h2 className="font-serif text-lg text-navy">
                What we are holding for you
              </h2>
            </div>
            {holdsCoverageDays && (
              <p className="mt-2 text-sm text-gray-600">
                We keep what your accounts record — not just sign-ins — so your
                team can look back through it instead of guessing. Here is how
                much of the last {home.coverage.window_days} days we actually
                hold.
              </p>
            )}
            <ul className="mt-3 space-y-2">
              {holdsCoverageDays &&
                home.coverage.platforms.map((platform) => (
                  <li
                    key={platform.platform}
                    className="rounded-lg bg-cream px-3 py-2 text-sm"
                  >
                    <span className="text-navy">{platform.label}</span>
                    <span className="text-gray-600">
                      {' — '}
                      {platform.days_held === 0
                        ? 'nothing held yet'
                        : `${platform.days_held} of the last ${home.coverage.window_days} days`}
                    </span>
                    {platform.kinds.length > 0 && (
                      <span className="block text-xs text-gray-500">
                        {coverageKindWords(platform.kinds)}
                      </span>
                    )}
                  </li>
                ))}

              {/* DW-CLIENT-CANARIES-IN-THE-SAME-PICTURE: the canary line sits
                  in the SAME list as Microsoft 365 and Google Workspace,
                  after them — one more thing we hold, not a separate widget
                  and not a replacement for either platform. The number is
                  `active_count` off the team brief and nothing else.

                  DW-CLIENT-HOME-WORD-DECOY-DOWNLOAD-IS-THE-ONE-HOLDING-TRIPWIRES-ROW:
                  and this row now SAYS WHAT A TRIPWIRE IS. It used to read
                  "decoys you saved into your own storage" — true, but it never
                  named the artefact, so a household read a count here and a
                  "Download Decoy (.docx)" button in the band above as two
                  different products. They are one thing: the Word document
                  they download is the canary in this picture, watched with the
                  365 and Workspace weeks named above it. The download VERB
                  stays where it lives (HomeTripwiresCard, in the band above) —
                  this row only names it, never repeats the button, and never
                  says Link.

                  DW-CLIENT-HOME-WORD-DECOY-FIRE-IS-THE-ONE-HOLDING-TRIPWIRES-ROW:
                  and this row is now also the FIRE. DigitalWatch.md: "canaries
                  are in the same picture — a tripwire the analyst sees WITH THE
                  LOGS, not a /client gimmick the model never reads"; GO_LIVE:
                  "a security team that KNOWS THE CLIENT, not a SIEM the client
                  operates" — the story lives on /client home as ONE holding
                  picture. An opened decoy used to be a SECOND cream box under
                  this list ("A tripwire fired. We did not act."), while this
                  row was already naming the very same Word document as a
                  count. One canary, told twice, on two surfaces: a household
                  read the documents here and had to read the fire separately
                  and work out that they were the same thing. That is the same
                  inventory-plus-brochure shape the sign-in, mail-rule/consent
                  and audit/directory folds already closed (see the tombstones
                  under this list), and this is the last of them.

                  Nothing was lost in the fold and nothing new is fetched: the
                  sentences are the box's own, word for word, off the SAME
                  `fired_count` / `last_fired_at` fields the box read. So the
                  row still:
                    * paints a fire ONLY where `fired_count` > 0. A missing
                      field is UNKNOWN and paints no fired story — never a
                      reassuring "nothing has ever fired" we did not verify —
                      and an unparseable `last_fired_at` costs the "most
                      recently ..." clause, never the fire;
                    * keeps the artefact and the count in the same breath: the
                      thing that was opened is the Word document they download
                      with Download Decoy (.docx), and the decoys still in
                      place are still said. The download VERB stays in
                      HomeTripwiresCard above — no second button in here;
                    * names NO one, NO place, NO device and NO detector,
                      because the picture holds none of them, and says out loud
                      that it will not invent one;
                    * claims NO action: an opened decoy read out loud is not a
                      response. Nothing taken off, nobody signed out, response
                      stays fail-closed — "We did not act.", the team decides
                      next steps, and this surface executes nothing;
                    * hands back NO console. Nobody is sent to /client/world to
                      go and work their own fire out. */}
              {heldTripwires && (
                <li className="rounded-lg bg-cream px-3 py-2 text-sm">
                  <span className="text-navy">Your tripwires</span>
                  {firedCount > 0 ? (
                    <>
                      <span className="text-gray-600">{' — '}</span>
                      <span className="text-gray-600">
                        A tripwire fired. We did not act.
                      </span>
                      <span className="block text-xs text-gray-500">
                        {firedCount === 1
                          ? 'One of the tripwires we hold for you has been opened'
                          : `${firedCount} of the tripwires we hold for you have been opened`}
                        {lastFiredWhen
                          ? `, most recently ${lastFiredWhen}`
                          : ''}
                        {
                          ' — that is the Word document you download above with '
                        }
                        Download Decoy (.docx), saved into your own storage, and
                        someone opened it.{' '}
                        {heldTripwires.active_count === 1
                          ? 'We hold 1 for you in all.'
                          : `We hold ${heldTripwires.active_count} for you in all.`}{' '}
                        We are not naming who opened it, where from or on what,
                        and we will not invent one. Nothing has been taken off
                        and nobody has been signed out. The team decides next
                        steps. This surface executes nothing.
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-gray-600">
                        {' — '}
                        {heldTripwires.active_count === 1
                          ? '1 in place'
                          : `${heldTripwires.active_count} in place`}
                      </span>
                      <span className="block text-xs text-gray-500">
                        each one is the Word document you download above with
                        Download Decoy (.docx), saved into your own storage and
                        watched with everything else here
                      </span>
                    </>
                  )}
                </li>
              )}

              {/* DW-CLIENT-HOME-CURRENT-CTI-SAME-PICTURE: this week's intel
                  desk, LAST in the same list — after Microsoft 365, Google
                  Workspace and the tripwires. It never displaces one of them
                  and never changes a number above it; it only adds the third
                  thing we hold, so the client sees current CTI and their own
                  firehose in one picture instead of two. The count is the
                  same applied-work list the watch card painted, so this line
                  can never claim a line they cannot go and read.

                  DW-CLIENT-HOME-INTEL-DESK-LINES-ARE-THE-ONE-HOLDING-INTEL-ROW:
                  and this row IS those lines now. It used to say only how MANY
                  the desk applied here, while the watch card in the band above
                  printed the lines themselves — one desk on two surfaces, a
                  ticker widget to operate beside a tally in the picture, which
                  is the SIEM the client operates. The card is gone from home
                  and the lines are printed here, verbatim, in the same list as
                  the 365/Workspace weeks and the Word decoys they are read
                  against. Where we hold no lines the row keeps the honest
                  count/silence it already painted — a count is never grown
                  into a campaign, and no line, CVE, kit or detector is
                  invented to fill a quiet week. Nothing is claimed as done:
                  reading this week's intel is looking, not acting. */}
              {(heldTickerLines.length > 0 || heldIntelDesk) && (
                <li className="rounded-lg bg-cream px-3 py-2 text-sm">
                  <span className="text-navy">This week's intel desk</span>
                  {heldTickerLines.length > 0 ? (
                    <>
                      <span className="text-gray-600">
                        {' — '}
                        what the world is doing now, read against everything
                        above
                        {intelDeskAsOf ? `, as of ${intelDeskAsOf}` : ''}
                      </span>
                      {heldTickerLines.map((line, i) => (
                        <span
                          key={i}
                          className="mt-1 block text-xs text-gray-500 whitespace-pre-wrap break-words"
                        >
                          {line}
                        </span>
                      ))}
                    </>
                  ) : heldIntelDesk ? (
                    <>
                      <span className="text-gray-600">
                        {' — '}
                        {heldIntelDesk.applied_line_count === 1
                          ? '1 line your analyst is applying to you'
                          : `${heldIntelDesk.applied_line_count} lines your analyst is applying to you`}
                        {intelDeskAsOf ? `, as of ${intelDeskAsOf}` : ''}
                      </span>
                      <span className="block text-xs text-gray-500">
                        what the world is doing now, read against everything
                        above
                      </span>
                    </>
                  ) : null}
                </li>
              )}

              {/* DW-CLIENT-HOME-KNOWN-TTPS-SAME-PICTURE: known TTPs land in
                  the SAME list, last — after Microsoft 365, Google Workspace,
                  the tripwires and this week's desk. It never displaces one of
                  them and never changes a number above it; it only completes
                  the loop, so the client sees their own firehose, current CTI
                  and what attackers are known to use in ONE picture instead of
                  three. The numbers are the backend's stored count of the
                  comparison it already ran, so this line can never claim work
                  that did not happen — and it says "lined up", never "caught".

                  DW-CLIENT-HOME-KNOWN-TTP-MATCHES-ARE-THE-ONE-HOLDING-TTPS-ROW:
                  and the row says that comparison as a FACT, in English, the
                  way every other row of this list does. It used to lead with an
                  operator fraction — "2 of 14 things we checked matched one" —
                  a scoreboard the household had to work out for itself, sitting
                  in the same picture as this week's intel desk, which is now
                  the desk's own lines in plain English. Two languages in one
                  picture: current CTI a household can read, known TTPs a
                  console they have to operate. That is the SIEM the client
                  operates, and it is the same shape the sign-in, mail-rule /
                  consent, audit / directory, Word-decoy and intel-desk folds
                  already closed.

                  Nothing new is fetched, counted or composed for this: the same
                  `known_ttps` object, said in the household's words. So:
                    * the row LEADS with what happened to them — their own
                      activity lined up with something attackers are known to
                      use — and says in the same breath that we did not act;
                    * the honest numbers stay, as facts under it: how much of
                      their activity we read, and how much of it lined up. They
                      are never grown into a technique, an indicator, a
                      campaign, a CVE, a kit, a detector or a rule id — the home
                      GET is counts on purpose, and this row invents none of the
                      rest;
                    * lining up is a COMPARISON, not a catch. Nothing was taken
                      off, nobody was signed out, response stays fail-closed;
                    * it is a fact we hold, not a console we hand back: no
                      button, no link, and nobody is sent to /client/world to
                      work their own techniques out. Their team reads it with
                      everything else in this one picture. */}
              {heldKnownTtps && (
                <li className="rounded-lg bg-cream px-3 py-2 text-sm">
                  <span className="text-navy">Known attacker techniques</span>
                  <span className="text-gray-600">{' — '}</span>
                  <span className="text-gray-600">
                    {heldKnownTtps.matched_count === 1
                      ? 'Something in your own activity lined up with a technique attackers are known to use. We did not act.'
                      : `${heldKnownTtps.matched_count} things in your own activity lined up with techniques attackers are known to use. We did not act.`}
                  </span>
                  <span className="block text-xs text-gray-500">
                    That is your own activity from the last{' '}
                    {heldKnownTtps.window_days} days, read against what
                    attackers are known to use —{' '}
                    {heldKnownTtps.compared_count === 1
                      ? 'one piece of it read in all'
                      : `${heldKnownTtps.compared_count} pieces of it read in all`}
                    . Lining up is a comparison, not a catch: we are not naming
                    the technique or the attacker here, and we will not invent
                    one to fill the gap. Nothing has been taken off and nobody
                    has been signed out. Your team reads it with everything else
                    on this page.
                  </span>
                </li>
              )}

              {/* DW-CLIENT-HOME-ESCALATE-IF-UNSURE: the last line of the same
                  list, after known TTPs — and the last sentence of the product
                  loop. It never displaces a line above it and never changes a
                  number above it; it only finishes the picture, so the client
                  sees what we hold, what we read it against, AND what we do
                  when it does not settle. The numbers are the backend's count
                  of judgements it already stored, and the row says the
                  disarmed truth in the product's own words: it went to a
                  person, and we did not act.

                  DW-CLIENT-HOME-ESCALATE-RESERVATIONS-ARE-THE-ONE-HOLDING-UNSURE-ROW:
                  and the row says that reservation as a FACT, in English, the
                  way every other row of this list does. It used to LEAD with an
                  operator fraction — "3 of 12 things we judged went to a
                  person" — a scoreboard the household had to work out for
                  itself, sitting in the same picture as known attacker
                  techniques, which is now that comparison in plain English.
                  Two languages in one picture: a reservation a household can
                  read, and a judgement console they have to operate. That is
                  the SIEM the client operates, and it is the same shape the
                  sign-in, mail-rule / consent, audit / directory, Word-decoy,
                  intel-desk and known-TTP folds already closed.
                  DigitalWatch.md: "escalate to a dearer SOC agent when unsure.
                  Act only within the armed/disarmed policy (response stays
                  fail-closed until we mean it)"; GO_LIVE: "a security team that
                  knows the client, not a SIEM the client operates" — the story
                  lives on /client home as ONE holding picture.

                  Nothing new is fetched, counted or composed for this: the same
                  `escalate_if_unsure` object, said in the household's words —
                  the very words the chat door already uses for this ("goes to a
                  person for a dearer read", never back as a clean bill). So:
                    * the row LEADS with what happened to them — something of
                      theirs did not settle, so it went to a person for a dearer
                      read — and says in the same breath that we did not act;
                    * the honest numbers stay, as facts under it: the window it
                      covers, and how much we judged in all. They are never
                      grown into a subject, a person, a place, a technique, an
                      indicator, a campaign, a CVE, a kit, a detector, a rule id
                      or a judgement id — the home GET is counts on purpose, and
                      this row invents none of the rest;
                    * going to a person is a dearer READ, never a catch and
                      never an action. Nothing was taken off, nobody was signed
                      out, response stays fail-closed, and an unsettled reading
                      is never handed back as reassurance;
                    * it is a fact we hold, not a console we hand back: no
                      button, no link, and nobody is sent to /client/world to
                      work their own reservations out;
                    * an omitted object — or a fraction that cannot be true —
                      still paints NO row, never a fabricated "0 things we
                      judged". */}
              {heldEscalateIfUnsure && (
                <li className="rounded-lg bg-cream px-3 py-2 text-sm">
                  <span className="text-navy">When we were not sure</span>
                  <span className="text-gray-600">{' — '}</span>
                  <span className="text-gray-600">
                    {heldEscalateIfUnsure.unsure_count === 1
                      ? 'Something of yours did not settle when we read it, so it went to a person for a dearer read. We did not act on it.'
                      : `${heldEscalateIfUnsure.unsure_count} things of yours did not settle when we read them, so they went to a person for a dearer read. We did not act on them.`}
                  </span>
                  <span className="block text-xs text-gray-500">
                    That is from the last {heldEscalateIfUnsure.window_days}{' '}
                    days, out of{' '}
                    {heldEscalateIfUnsure.judged_count === 1
                      ? 'the one thing we judged in all'
                      : `${heldEscalateIfUnsure.judged_count} things we judged in all`}
                    . Going to a person is a dearer read, not a catch and not
                    something done for you: we are not naming what it was, who
                    it concerned or where it came from, and we will not invent
                    one to fill the gap. Nothing has been taken off and nobody
                    has been signed out. Your team reads it with everything else
                    on this page.
                  </span>
                </li>
              )}

              {/* DW-CLIENT-HOME-POL-KINDS-IN-HOLDING-PICTURE: the FIRST clause
                  of the product loop — "know the person: regular chat,
                  patterns of life, where they sign in" — said in the SAME
                  list as Microsoft 365, Google Workspace and the tripwires.
                  It used to sit in a second card of its own ("What we know
                  about your world"), which made the person we claim to know a
                  separate product from the firehose we hold for them: a
                  household read 365, Workspace and their decoys here, then
                  had to scroll to another heading to learn we also hold
                  people, how their world normally runs, and who we wake. One
                  picture, one team.

                  Honest by construction, and nothing new is fetched or
                  counted: the number is `pol_summary_count` and the kinds are
                  `pol_summary_kinds`, both already on this same brief, both
                  read by the backend off the very rows it counted. So:
                    * no count (or a count that is not a number) → no line,
                      never a fabricated zero;
                    * a count of 0 → no line either. We have written nothing
                      about this household yet, and silence is the honest read
                      of that — it is never grown into people or rhythms;
                    * kinds are categories, never stored content: no person,
                      no rhythm, no city, no device, no phone travels with
                      them, and an empty or absent list leaves the row on the
                      count alone rather than inventing a plausible kind;
                    * the inert response-consent rows are not a spoken kind,
                      so a household whose only held section is that one reads
                      the count and nothing more — response is disarmed, and
                      this row never implies otherwise.

                  It is a fact we own, not a console we hand back: no button,
                  no link, and nothing here sends the household off to operate
                  /client/world for the person we say we know.

                  DW-CLIENT-HOME-POL-KINDS-NOT-A-SECOND-PRODUCT-NAME: and the
                  row is a FACT of this list, not a product inside it. It kept
                  the dead card's title — "What we know about your world" — as
                  its own subject, so the one thing we moved in here still read
                  as a second heading nested under "What we are holding for
                  you": 365, Workspace and their decoys are plainly things we
                  hold, and then a brochure line about knowing them. The mouth
                  had already been told these kinds are one line of the holding
                  picture beside those three. The subject is now the same
                  register as its neighbours — the thing itself, "Your patterns
                  of life", the product loop's own words — and the count and
                  the kinds under it are unchanged. No second product name, no
                  second card, no second place their world lives. */}
              {heldPolCount !== null && (
                <li className="rounded-lg bg-cream px-3 py-2 text-sm">
                  <span className="text-navy">Your patterns of life</span>
                  <span className="text-gray-600">
                    {' — '}
                    {heldPolCount === 1
                      ? '1 thing your team has written about your world'
                      : `${heldPolCount} things your team has written about your world`}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {polKinds.length > 0
                      ? `the kinds we are holding: ${joinKinds(polKinds)}. `
                      : 'that is the count we are holding, and it is all of it we say here. '}
                    We are not naming a person, a rhythm, a place or a device
                    here, and we will not invent one to fill the gap. Knowing
                    you is our job, not a page for you to keep up to date — if
                    we have something wrong, tell your team in a chat and we
                    will fix it.
                  </span>
                </li>
              )}

              {/* DW-CLIENT-HOME-SIGNIN-PLACES-NOT-A-SECOND-CARD: the rest of
                  the loop's first clause — "know the person: regular chat,
                  patterns of life, WHERE THEY SIGN IN. Graph/Workspace
                  locations are FACTS. The mouth does not invent cities" —
                  said in the SAME list as Microsoft 365, Google Workspace,
                  the tripwires and their patterns of life, and LAST, so it
                  displaces none of them. The labels used to be a second card
                  of their own ("Where we see you sign in") sitting above this
                  one: a household read their clouds, their decoys and their
                  world in the holding picture, and then read the places they
                  sign in from somewhere else, as though where they sign in
                  were a product they operate rather than a fact we hold for
                  them. One picture, one team.

                  DW-CLIENT-HOME-SIGNIN-ENGLISH-NOT-A-SECOND-BOX: and this
                  row is now the ONLY sign-in story on home. The English that
                  used to sit under this list in a second cream box ("We have
                  seen sign-ins on your accounts. We did not act.") said the
                  same held records over again, and named the same places off
                  the same field — one set of records, told twice, which is
                  the SIEM the client operates. That box is gone and the
                  sentences it owed the household are folded in here: a
                  sign-in that went through is still not proof it was you, we
                  did not act, nobody has been signed out, and no sign-in has
                  been stopped. So this row now paints on held sign-in RECORDS
                  as well as on held places — the box spoke for those
                  households and something has to, or folding it would have
                  quietly dropped MFA-success-is-never-safe off the picture
                  for them. It is the same one line either way: no second
                  surface, and no inventory added.

                  Nothing is fetched, counted or composed for this: the labels
                  are `signin_locations.places` off this same brief — the very
                  field the dead card read — rendered VERBATIM and joined in
                  English, and the records half is the SAME pooled coverage
                  kind (`holdsSignInRecords`) the dead box read. So:
                    * NO PLACES → no city, anywhere. We have not seen where
                      this person signs in, and an unknown is not an empty
                      world: there is no fallback city, no country, no
                      "somewhere in New Zealand", and — load-bearing — no "we
                      hold none", which would be an all-clear we did not
                      verify. Where we hold sign-in RECORDS but no place, the
                      row still says the records and says out loud that it is
                      naming no place, device or person; where we hold
                      NEITHER, there is NO ROW and no sentence either;
                    * nothing is re-cased, tidied, completed or geocoded on
                      the way in. The only labels dropped are non-strings and
                      blanks, which are not places;
                    * the list is said to be all of it we hold, which is what
                      the chat already tells them — home and the mouth agree;
                    * it is a fact we own, not a console we hand back: no
                      button, no link, and nobody is sent off to /client/world
                      to keep their own places up to date;
                    * naming where they signed in is never naming it safe. The
                      row says out loud that a sign-in going through every
                      check is not proof it was them, and claims no action —
                      nobody was signed out, response stays fail-closed. */}
              {(holdsSignInRecords || heldSignInPlaces.length > 0) && (
                <li className="rounded-lg bg-cream px-3 py-2 text-sm">
                  <span className="text-navy">Where you sign in</span>
                  <span className="text-gray-600">
                    {' — '}
                    {heldSignInPlaces.length > 0
                      ? heldSignInPlacesEnglish
                      : 'the sign-ins your own records hold'}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {heldSignInPlaces.length > 0
                      ? heldSignInPlaces.length === 1
                        ? 'the place your own records put your sign-ins in, and no other'
                        : 'the places your own records put your sign-ins in, and no others'
                      : 'we are not naming a place, a device or a person here'}
                    {' — '}we did not work one out for you and we will not
                    invent one.{' '}
                    {heldSignInPlaces.length > 0 ? (
                      <>
                        A sign-in from{' '}
                        {heldSignInPlaces.length === 1 ? 'it' : 'one of them'}{' '}
                        is still not proof it was you, and we did not act.
                      </>
                    ) : (
                      'A sign-in that went through every check is still not proof it was you, and we did not act.'
                    )}{' '}
                    Nobody has been signed out, and no sign-in has been stopped.
                  </span>
                </li>
              )}
              {/* DW-CLIENT-HOME-MAIL-RULE-CONSENT-ENGLISH-NOT-A-SECOND-BOX:
                  the mail-rule / consent English, said as a ROW of this list
                  and nowhere else. GO_LIVE: "a security team that KNOWS THE
                  CLIENT, not a SIEM the client operates" — the story lives on
                  /client home as ONE holding picture.

                  It used to be a second cream box UNDER this list, while the
                  Microsoft 365 and Google Workspace rows above already named
                  "mail rules" and "app consents" as kinds we hold. One set of
                  records, told twice, on two surfaces: the household had to
                  read the page twice and work out which one was the answer.
                  That is the inventory-plus-brochure shape the sign-in fold
                  already closed (see the tombstone under this list), and this
                  is the same fold for the same reason.

                  Nothing was lost in the move and nothing new is fetched: the
                  sentences are the box's own, word for word, off the SAME
                  pooled coverage kinds (`holdsMailRuleRecords`,
                  `holdsConsentRecords`) the box read. So the row still:
                    * exists only where the KIND exists, and says mail and
                      access separately — holding mail-rule records never
                      speaks for consents, and the reverse. Coverage naming
                      neither kind gets NO row and — load-bearing — no "there
                      isn't one" either. An absence we did not verify is not
                      an all-clear;
                    * names NO rule, NO app, NO address, NO mailbox, NO person
                      and NO cloud, because the picture holds none of them,
                      and says out loud that it will not invent one;
                    * claims NO action: saying a rule out loud is not taking
                      it off. Nothing removed, no permission taken back,
                      nobody signed out — response stays fail-closed;
                    * hands back NO console. We hold the weeks; reading them
                      is our job, not a chore for the household to run. */}
              {holdsMailRuleOrConsent && (
                <li className="rounded-lg bg-cream px-3 py-2 text-sm">
                  <span className="text-navy">
                    {holdsMailRuleRecords && holdsConsentRecords
                      ? 'Rules on your mail, and apps with access'
                      : holdsMailRuleRecords
                        ? 'Rules on your mail'
                        : 'Apps with access to your account'}
                  </span>
                  <span className="text-gray-600">{' — '}</span>
                  <span className="text-gray-600">
                    {holdsMailRuleRecords && holdsConsentRecords
                      ? 'A rule was made on your mail, and an app was given access to your account. We did not act.'
                      : holdsMailRuleRecords
                        ? 'A rule was made on your mail. We did not act.'
                        : 'An app was given access to your account. We did not act.'}
                  </span>
                  <span className="block text-xs text-gray-500">
                    That is what your own records say, inside the last{' '}
                    {home.coverage.window_days} days we hold — in plain English
                    rather than in record names. We are not naming the rule or
                    the app here, and we will not invent a name to fill the gap.
                    Nothing has been taken off, no permission has been taken
                    back, and nobody has been signed out.
                  </span>
                </li>
              )}

              {/* DW-CLIENT-HOME-AUDIT-DIRECTORY-ENGLISH-NOT-A-SECOND-BOX:
                  the audit / directory English, said as a ROW of this list and
                  nowhere else. GO_LIVE: "a security team that KNOWS THE
                  CLIENT, not a SIEM the client operates" — the story lives on
                  /client home as ONE holding picture.

                  It used to be a second cream box UNDER this list, while the
                  Microsoft 365 and Google Workspace rows above already named
                  "account activity" and "directory changes" as kinds we hold.
                  One set of records, told twice, on two surfaces: the
                  household had to read the page twice and work out which one
                  was the answer. That is the inventory-plus-brochure shape the
                  sign-in fold and then the mail-rule/consent fold already
                  closed (see the tombstones under this list), and this is the
                  same fold for the same reason — the last of the second boxes.

                  Nothing was lost in the move and nothing new is fetched: the
                  sentences are the box's own, word for word, off the SAME
                  pooled coverage kinds (`holdsAuditRecords`,
                  `holdsDirectoryRecords`) the box read. So the row still:
                    * exists only where the KIND exists, and says administrative
                      work and the directory separately — holding audit records
                      never speaks for directory changes, and the reverse.
                      Coverage naming neither kind gets NO row and —
                      load-bearing — no "there isn't one" either. An absence we
                      did not verify is not an all-clear;
                    * names NO change, NO operation, NO role, NO group, NO
                      person and NO cloud, because the picture holds none of
                      them, and says out loud that it will not invent one;
                    * claims NO action: saying a change out loud is not undoing
                      it. Nothing put back, no change reversed, nobody signed
                      out — response stays fail-closed;
                    * hands back NO console. We hold the weeks; reading them is
                      our job, not a chore for the household to run. */}
              {holdsAuditOrDirectory && (
                <li className="rounded-lg bg-cream px-3 py-2 text-sm">
                  <span className="text-navy">
                    {holdsAuditRecords && holdsDirectoryRecords
                      ? 'Administrative work on your accounts, and changes to your directory'
                      : holdsAuditRecords
                        ? 'Administrative work on your accounts'
                        : 'Changes to your directory'}
                  </span>
                  <span className="text-gray-600">{' — '}</span>
                  <span className="text-gray-600">
                    {holdsAuditRecords && holdsDirectoryRecords
                      ? 'There was administrative activity on your accounts, and someone changed how your directory works. We did not act.'
                      : holdsAuditRecords
                        ? 'There was administrative activity on your accounts. We did not act.'
                        : 'Someone changed how your directory works. We did not act.'}
                  </span>
                  <span className="block text-xs text-gray-500">
                    That is what your own records say, inside the last{' '}
                    {home.coverage.window_days} days we hold — in plain English
                    rather than in record names. We are not naming the change or
                    the person here, and we will not invent a name to fill the
                    gap. Nothing has been put back the way it was, no change has
                    been reversed, and nobody has been signed out.
                  </span>
                </li>
              )}
              {/* DW-CLIENT-HOME-FAIL-CLOSED-POLICY-IS-THE-ONE-HOLDING-ROW:
                  the fail-closed policy, said as a ROW of this list — the
                  LAST one, under everything we hold, because it is the rule
                  that governs every row above it. DigitalWatch.md: "escalate
                  to a dearer SOC agent when unsure. ACT ONLY WITHIN THE
                  ARMED/DISARMED POLICY (response stays fail-closed until we
                  mean it)"; GO_LIVE: "a security team that knows the client,
                  not a SIEM the client operates" — the story lives on /client
                  home as ONE holding picture.

                  The other half of that loop sentence was already a held fact
                  the household can read: "When we were not sure" is the
                  reservation in English, a row of this same list. This half
                  was not. It lived only in the "Talk to your team" section
                  above — a paragraph about what the chat behind that door is
                  handed in every turn — so the household read what we hold in
                  one register and the promise that we do not act on it in
                  another, over by the chat door, as a lecture rather than a
                  fact of the picture. Two surfaces for one policy, and the
                  half that has to stand on a quiet week was the one they had
                  to go looking for. That is the SIEM the client operates, and
                  this is the same fold the sign-in, mail-rule / consent,
                  audit / directory, Word-decoy, intel-desk, known-TTP and
                  reservation rows already closed.

                  The door keeps its own line — it is a claim about what the
                  MODEL is given in every conversation, which is not what this
                  row says — exactly as the door still says the unsure rule
                  and this week's lines reach the team beside their own rows
                  here. This row is the household's copy: the policy as a
                  thing we hold for them, in the door's own present-tense
                  English.

                  Nothing is fetched, counted or composed for it, and it takes
                  no field: the policy is read off how the product is built,
                  not off this brief. So the row:
                    * is UNCONDITIONAL — no "if", no count, no window and no
                      row of data behind it. It is a fact of this picture on a
                      quiet week and on a busy one, which is the whole point:
                      the days nothing happened are exactly the days a promise
                      hanging off rows that are not there would go unsaid;
                    * is PRESENT TENSE, because we are disarmed TODAY. It says
                      what we do now, not what we will always do — the day the
                      armed/disarmed policy changes, this row changes with it,
                      and it never speaks response_consent as though response
                      were armed;
                    * is a POLICY, never a reading: it claims nothing about
                      what we found, names no thing, no person, no place, no
                      technique, no indicator, no campaign, no CVE, no kit, no
                      detector, no rule id and no judgement id, and it is not
                      an all-clear — the rows above it say what we hold, and
                      this one only says what we do not do with it;
                    * claims NO action, in either direction: nothing taken
                      off, no permission taken back, nobody signed out, no
                      sign-in stopped. Response stays fail-closed;
                    * hands back NO console: no button, no link, nothing to
                      switch on, and nobody sent to /client/world to arm
                      anything. The download verb stays where it lives, on the
                      home tripwires section above, and is never repeated in
                      here. Their team says what they recommend, and the
                      household decides what happens next. */}
              <li className="rounded-lg bg-cream px-3 py-2 text-sm">
                <span className="text-navy">We do not act on your account</span>
                <span className="text-gray-600">{' — '}</span>
                <span className="text-gray-600">
                  Not on a quiet week and not on a busy one. Your team will tell
                  you what we can see and what we recommend, and you decide what
                  happens next.
                </span>
                <span className="block text-xs text-gray-500">
                  That is how we work today, and it holds for everything above
                  and for anything we have not written here: nothing is taken
                  off, no permission is taken back, nobody is signed out and
                  nobody is stopped from signing in. It is not something you
                  have to ask for or switch on, and it is not a reading of what
                  we found — we are not naming a thing, a person or a place
                  here. Where we think something should be done, your team says
                  so to you in your own words, and you decide.
                </span>
              </li>
            </ul>

            {/* DW-CLIENT-HOME-WORD-DECOY-FIRE-IS-THE-ONE-HOLDING-TRIPWIRES-ROW:
                the fired-tripwire English used to live HERE, in a cream box
                under the list — "A tripwire fired. We did not act." / "One of
                the tripwires we hold for you has been opened" — while the
                "Your tripwires" row above it already named the very same Word
                document they download as a count we hold. One canary on two
                surfaces: a count in the holding picture, and a fire card the
                household had to read separately and join up itself. That is
                the SIEM the client operates.

                The English is now folded into that one row and this box is
                gone. Nothing was lost in the fold and nothing new was fetched:
                the row still says "A tripwire fired. We did not act." word for
                word off the same `fired_count`, still says how many were
                opened and when where `last_fired_at` parses, still names the
                artefact as the Word document they download with Download Decoy
                (.docx) — the verb itself staying in HomeTripwiresCard above,
                never repeated in here — still names nobody, and still claims
                nothing: the team decides next steps, this surface executes
                nothing, response stays fail-closed. The sign-in, mail-rule /
                consent and audit / directory English were folded into rows of
                this same one list before it. */}

            {/* DW-CLIENT-HOME-SIGNIN-ENGLISH-NOT-A-SECOND-BOX: the sign-in
                English used to live HERE, in a cream box under the list — "We
                have seen sign-ins on your accounts. We did not act." — while
                the list above it already carried the same held records as the
                "Where you sign in" row, naming the same places off the same
                field. Two surfaces telling one story about one set of records
                is the SIEM the client operates: the household had to read the
                page twice and decide which one was the answer. The English is
                now folded into that one row, and this box is gone.

                Nothing was lost in the fold and nothing new was fetched: the
                row still says the places VERBATIM where we hold them, still
                says nothing at all about cities where we do not, still says a
                sign-in that went through every check is not proof it was you,
                and still says we did not act — nobody signed out, no sign-in
                stopped, response fail-closed. The mail-rule/consent English
                and the audit/directory English are their own records, and
                have since been folded into rows of the same one list. */}

            {/* DW-CLIENT-HOME-MAIL-RULE-CONSENT-ENGLISH-NOT-A-SECOND-BOX:
                the mail-rule / consent English used to live HERE, in a second
                cream box under the list — "A rule was made on your mail" /
                "An app was given access to your account" — while the 365 and
                Workspace rows in the list above already named "mail rules"
                and "app consents" as kinds we hold. Two surfaces telling one
                story about one set of records is the SIEM the client
                operates. The English is now a ROW of "What we are holding for
                you", and this box is gone.

                Nothing was lost in the fold and nothing new was fetched: the
                row carries the box's sentences verbatim, still says them only
                where the coverage kind says we hold that record, still says
                mail and access separately, still names no rule and no app and
                says it will not invent one, and still says we did not act —
                nothing taken off, no permission taken back, nobody signed
                out, response fail-closed. The audit/directory English is its
                own records, and has since been folded into a row of this
                same list on exactly these terms. */}

            {/* DW-CLIENT-HOME-AUDIT-DIRECTORY-ENGLISH-NOT-A-SECOND-BOX:
                the audit / directory English used to live HERE, in a second
                cream box under the list — "There was administrative activity
                on your accounts" / "Someone changed how your directory works"
                — while the 365 and Workspace rows in the list above already
                named "account activity" and "directory changes" as kinds we
                hold. Two surfaces telling one story about one set of records
                is the SIEM the client operates. The English is now a ROW of
                "What we are holding for you", and this box is gone. With it
                goes the last second box under this list: home now tells one
                sign-in story, one mail-rule/consent story and one
                audit/directory story, all of them rows of the one picture.

                Nothing was lost in the fold and nothing new was fetched: the
                row carries the box's sentences verbatim, still says them only
                where the coverage kind says we hold that record, still says
                administrative work and the directory separately, still names
                no change and no person and says it will not invent one, and
                still claims no action — nothing put back the way it was, no
                change reversed, nobody signed out, response fail-closed. */}
          </section>
        ) : null}

        <div className="grid gap-x-5 lg:grid-cols-3">
          {/* The team's own reading, live. */}
          <div className="lg:col-span-2">
            {/* Calm home status — the team's plain-language reading, live. Only
                rendered once we know who is asking; without a principal id there is
                no honest status to fetch. */}
            {principalId && <HomeStatusCard principalId={principalId} />}
          </div>

          {/* The quieter side of home: what the team is asking for or
              offering, and the steps still open. Every one of these renders
              nothing at all when there is nothing real to say. */}
          <aside className="lg:col-span-1">
            {/* Mailbox coverage (org-admin only) — Concierge line when seats have
                stopped reporting. All-clear / fetch fail / non-admin → nothing. */}
            <HomeMailboxCoverageLine />

            {/* Idle advisory — the team's gentle, optional offers tied to real
                facts ("care, not spam"). Only when we know who is asking; empty →
                renders nothing, so the calm home stays calm. */}
            {principalId && <IdleAdvisoryCard principalId={principalId} />}

            {/* Context-Keeper ask — one open question from the team, answerable
                here so continuous learning is not a hidden sidebar. Empty → nothing. */}
            {principalId && <HomeContextAskCard principalId={principalId} />}

            {/* Organisation-access step — only mounted once the gate says home
                (completed first meeting), same as this-poll. No stored SMB
                session token → nothing renders and nothing is fetched. */}
            <SmbAllowStep />
          </aside>
        </div>
    </SocShell>
  )
}
